import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { salesPipelineAPI, operationsAPI, adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus, HiOutlineSearch,
  HiOutlinePencil, HiOutlineTrash, HiOutlineX, HiOutlineSave, HiOutlineChatAlt2,
  HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlineClipboardList, HiOutlineShieldCheck,
  HiOutlinePrinter, HiOutlineEye, HiOutlineDocumentText, HiOutlineDownload
} from 'react-icons/hi';
import { useAuth } from '../../context/AuthContext';
import { formatDragChainOrderCode, formatMountingBracketCode } from '../../utils/productNameHelper';
import OrderWorkflowBanner from '../../components/common/OrderWorkflowBanner';
import ApproveSalesOrderModal from '../../components/common/ApproveSalesOrderModal';
import RecordDetailsModal, { ActionButton } from '../../components/common/RecordDetailsModal';
import { formatPaymentBasis, getReleasedBy, formatRole, formatDateTime } from '../../utils/salesOrderRelease';

const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#6366f1', icon: '🆕', desc: 'Fresh unconverted inquiry' },
  { value: 'contacted', label: 'Contacted', color: '#3b82f6', icon: '📞', desc: 'Initial contact made with prospect' },
  { value: 'working', label: 'Working / In Progress', color: '#0ea5e9', icon: '⚙️', desc: 'Actively discussing requirement' },
  { value: 'qualified', label: 'Qualified', color: '#10b981', icon: '✅', desc: 'Meets criteria / ready for quote' },
  { value: 'unqualified', label: 'Unqualified / Disqualified', color: '#ef4444', icon: '❌', desc: 'Budget, competitor, or dropped' },
  { value: 'nurturing', label: 'Nurturing', color: '#8b5cf6', icon: '🌱', desc: 'Future opportunity' }
];

const OPPORTUNITY_TYPE_OPTIONS = [
  { value: 'new_business', label: 'New Business', icon: '💼' },
  { value: 'existing_business', label: 'Existing Business / Repeat', icon: '🔄' },
  { value: 'replacement', label: 'Replacement Requirement', icon: '🛠️' },
  { value: 'oem_partner', label: 'OEM Machine Builder', icon: '🤝' },
  { value: 'expansion', label: 'Line Expansion / Project Add-on', icon: '📈' },
  { value: 'other', label: 'Other Inquiry', icon: '📌' }
];

const RATING_OPTIONS = [
  { value: 'hot', label: 'Hot', icon: '🔥', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', desc: 'Ready to buy (7-15 days)' },
  { value: 'warm', label: 'Warm', icon: '🌤️', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', desc: 'Interested, evaluating budget' },
  { value: 'cold', label: 'Cold', icon: '❄️', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', desc: 'Early inquiry / long timeline' }
];

const LIFECYCLE_STAGES = [
  { key: 'lead', label: 'Lead', icon: '🎯', desc: 'Raw contact' },
  { key: 'mql', label: 'MQL', icon: '📊', desc: 'Marketing Qualified' },
  { key: 'sql', label: 'SQL', icon: '💼', desc: 'Sales Qualified' },
  { key: 'opportunity', label: 'Opportunity', icon: '🚀', desc: 'Deal In Play' },
  { key: 'customer', label: 'Customer', icon: '🏆', desc: 'Won Customer' }
];

const REJECTION_REASONS = [
  { value: 'competitor_price', label: 'Competitor Price Lower' },
  { value: 'spec_mismatch', label: 'Technical Spec Mismatch' },
  { value: 'project_cancelled', label: 'Project Cancelled / On Hold' },
  { value: 'budget_constraint', label: 'Budget Not Allocated' },
  { value: 'no_response', label: 'Client Not Responding' },
  { value: 'quality_issue', label: 'Sample / Trial Rejected' },
  { value: 'other', label: 'Other Reason' }
];

const LEAD_SOURCES = [
  'Direct Inquiry',
  'Website / Portal',
  'Cold Call',
  'Customer Referral',
  'Trade Show / Expo',
  'WhatsApp Business',
  'Inbound Email',
  'Indiamart / TradeIndia',
  'Distributor / Partner',
  'Other'
];

const STAGE_CONFIG = {
  lead:        { label: 'Lead',        color: '#6366f1', bg: 'rgba(99,102,241,0.15)',  icon: '🎯' },
  quotation:   { label: 'Quotation',   color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', icon: '📜' },
  pi:          { label: 'Proforma Inv (PI)', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: '📄' },
  customer_po: { label: 'Customer PO', color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)', icon: '📥' },
  sales_order: { label: 'Sales Order', color: '#22c55e', bg: 'rgba(34,197,94,0.15)',  icon: '🚀' }
};

const DOC_TYPE_LABELS = {
  tax_invoice: 'Tax Invoice (GST)',
  standard_bill: 'Standard Bill (Non-GST)',
  non_gst_bill: 'Standard Bill (Non-GST)',
  challan: 'Delivery Challan',
  quotation: 'Quotation',
  pi: 'Proforma Invoice (PI)'
};

const APPROVAL_CONFIG = {
  approved: { label: 'Approved', icon: '✅', color: '#16a34a', bg: 'rgba(34,197,94,0.12)' },
  rejected: { label: 'Rejected', icon: '❌', color: '#dc2626', bg: 'rgba(239,68,68,0.12)' },
  pending:  { label: 'Awaiting Admin Approval', icon: '⏳', color: '#ca8a04', bg: 'rgba(234,179,8,0.14)' }
};

// Sales Orders that aren't approved or rejected are waiting on Admin
const getApproval = (rec) => (rec.stage === 'sales_order' ? (APPROVAL_CONFIG[rec.approvalStatus] || APPROVAL_CONFIG.pending) : null);

const STORE_STATUS_LABELS = {
  assemble: '🔧 Assembly in Store',
  packing: '📦 Packing Station',
  hold: '⏸️ On Hold in Store',
  dispatched: '🚚 Dispatched'
};

const CHAIN_TYPE_LABELS = { OP: 'OP — Open', CP: 'CP — Closed', SEMI: 'SEMI — Semi-closed' };

// The number that identifies the record at its current stage
const getRecordNumber = (rec) => {
  const byStage = { sales_order: rec.salesOrderNumber, pi: rec.piNumber, customer_po: rec.customerPoNumber, quotation: rec.quotationNumber };
  return byStage[rec.stage] || rec.salesOrderNumber || rec.quotationNumber || rec.piNumber
    || `${rec.stage === 'lead' ? 'LEAD' : 'REC'}-${String(rec._id || '').slice(-6).toUpperCase()}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fmtMoney = (n) => (n === undefined || n === null || n === '' ? '' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const humanize = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');

// Model specifications, pitch, links per meter and pricing are loaded dynamically from MongoDB ProductTemplates.
const getSpecFromModel = (modelNumber = '', templates = []) => {
  if (!modelNumber) return { pitch: 0, linksPerMeter: 0, ratePerMeter: 0, defaultWidth: '' };
  const raw = modelNumber.toString().trim();
  const clean = raw.toUpperCase().replace(/\s+/g, '');

  if (Array.isArray(templates) && templates.length > 0) {
    // 1. Exact match by modelNumber
    let match = templates.find(t => {
      if (!t.modelNumber) return false;
      const tClean = t.modelNumber.toUpperCase().replace(/\s+/g, '');
      return t.modelNumber === raw || tClean === clean;
    });

    // 2. Prioritized match (longest/most specific model number first)
    if (!match) {
      const sortedTemplates = [...templates].sort((a, b) => (b.modelNumber || '').length - (a.modelNumber || '').length);
      match = sortedTemplates.find(t => {
        if (!t.modelNumber) return false;
        const tClean = t.modelNumber.toUpperCase().replace(/[\s-_]/g, '');
        const targetClean = clean.replace(/[\s-_]/g, '');
        return targetClean.includes(tClean) || tClean.includes(targetClean);
      });
    }

    if (match) {
      const pitch = parseFloat(match.pitch) || 0;
      const links = parseInt(match.linksPerMeter || match.numberOfLinks, 10) || (pitch > 0 ? Math.ceil(1000 / pitch) : 0);
      const rate = parseFloat(match.ratePerMeter || match.unitPrice) || 0;
      const width = match.width || match.innerSize || '';
      return {
        pitch,
        linksPerMeter: links,
        ratePerMeter: rate,
        defaultWidth: width,
        series: match.modelNumber
      };
    }
  }

  return { pitch: 0, linksPerMeter: 0, ratePerMeter: 0, defaultWidth: '', series: modelNumber };
};

const recalculateAll = (currentForm, changedField = '', changedValue = null, templates = []) => {
  const updated = { 
    ...currentForm, 
    dragChainConfig: { ...(currentForm.dragChainConfig || {}) } 
  };

  // Apply change if specified
  if (changedField.startsWith('dragChainConfig.')) {
    const sub = changedField.split('.')[1];
    updated.dragChainConfig[sub] = changedValue;
  } else if (changedField) {
    updated[changedField] = changedValue;
  }

  const cfg = updated.dragChainConfig;
  const spec = getSpecFromModel(cfg.modelNumber, templates);

  // 1. Pitch & Link calculation
  let pitch = parseFloat(cfg.pitch);
  if (!pitch || isNaN(pitch) || pitch <= 0) {
    pitch = spec.pitch || 46;
  }
  cfg.pitch = pitch;

  // Set quantity: how many number of sets required by customer
  let setQty = 1;
  if (cfg.quantity !== undefined && cfg.quantity !== null && cfg.quantity !== '') {
    const parsedQty = parseFloat(cfg.quantity);
    if (!isNaN(parsedQty) && parsedQty > 0) {
      setQty = parsedQty;
    }
  }
  cfg.quantity = setQty;

  // Parse length per set in millimeters
  let setLengthMm = 1000;
  if (cfg.length) {
    const cleanLen = String(cfg.length).trim().toLowerCase();
    const num = parseFloat(cleanLen.replace(/[^0-9.]/g, ''));
    if (!isNaN(num) && num > 0) {
      if ((cleanLen.includes('m') && !cleanLen.includes('mm')) || (num < 20 && !cleanLen.includes('mm'))) {
        setLengthMm = Math.round(num * 1000);
      } else {
        setLengthMm = Math.round(num);
      }
    }
  } else {
    cfg.length = '1000 MM';
  }

  if (changedField === 'dragChainConfig.numberOfLinks' || changedField === 'dragChainConfig.totalCalculatedLinks') {
    // Reverse calculation: User typed total links
    const totalLinks = parseInt(changedValue, 10) || 0;
    if (totalLinks > 0) {
      const totalLengthMm = Math.round(totalLinks * pitch);
      setLengthMm = Math.round(totalLengthMm / setQty);
      cfg.length = `${setLengthMm} MM`;
      const totalMeters = Math.round((totalLengthMm / 1000) * 100) / 100;
      cfg.totalMeters = totalMeters;
      cfg.linksPerSet = Math.ceil(setLengthMm / pitch);
      cfg.totalCalculatedLinks = totalLinks;
    }
  } else {
    // Length per set or Set Quantity changed -> calculate total meters & links
    const totalLengthMm = setLengthMm * setQty;
    const totalMeters = Math.round((totalLengthMm / 1000) * 100) / 100;
    cfg.totalMeters = totalMeters;

    const linksPerSet = Math.ceil(setLengthMm / pitch);
    const totalLinks = Math.ceil(totalLengthMm / pitch);
    cfg.linksPerSet = linksPerSet;
    cfg.totalCalculatedLinks = totalLinks;
    cfg.numberOfLinks = totalLinks;
  }

  // 2. Base Rate / meter shouldn't fixed (Editable rate per meter)
  let ratePerMeter = Number(cfg.ratePerMeter);
  if (isNaN(ratePerMeter) || ratePerMeter <= 0) {
    ratePerMeter = Number(updated.ratePerMeter) > 0 ? Number(updated.ratePerMeter) : (spec.ratePerMeter || 1200);
  }
  cfg.ratePerMeter = ratePerMeter;
  updated.ratePerMeter = ratePerMeter;

  const totalMeters = cfg.totalMeters || (setLengthMm * setQty / 1000);
  const grossAmount = Math.round((totalMeters * ratePerMeter) * 100) / 100;
  cfg.grossAmount = grossAmount;
  updated.grossAmount = grossAmount;

  const discountPercent = Math.min(100, Math.max(0, Number(cfg.discountPercent ?? updated.discountPercent ?? 0)));
  cfg.discountPercent = discountPercent;
  updated.discountPercent = discountPercent;

  const discountAmount = Math.round((grossAmount * (discountPercent / 100)) * 100) / 100;
  cfg.discountAmount = discountAmount;
  updated.discountAmount = discountAmount;

  const taxableAmount = Math.max(0, Math.round((grossAmount - discountAmount) * 100) / 100);
  updated.totalAmount = taxableAmount;

  const isNonGst = updated.orderDocumentType === 'standard_bill' || updated.orderDocumentType === 'non_gst_bill';
  const gstRate = isNonGst ? 0 : (Number(updated.taxRate ?? 18) / 100);
  const gstAmount = isNonGst ? 0 : Math.round((taxableAmount * gstRate) * 100) / 100;
  updated.gstAmount = gstAmount;
  updated.grandTotal = Math.round((taxableAmount + gstAmount + Number(updated.shippingCharges || 0)) * 100) / 100;

  return updated;
};

const BLANK_FORM = {
  stage: 'sales_order', customerName: '', salesPersonName: '', customerContact: '', customerEmail: '', customerAddress: '', customerGST: '',
  billingAddress: '', shippingAddress: '', isSameAsBilling: true,
  quotationNumber: '', quotationDate: '', piNumber: '', customerPoNumber: '', salesOrderNumber: '',

  // Salesforce Lead Fields
  leadOwner: '',
  leadOwnerName: '',
  leadSource: 'Direct Inquiry',
  leadStatus: 'new',
  opportunityType: 'new_business',
  rating: 'warm',
  nextSteps: '',
  nextFollowUpDate: '',
  rejectionCategory: '',
  lifecycleStage: 'lead',
  
  // Document Type & Invoicing vs Challan vs Quotation
  orderDocumentType: 'tax_invoice', // 'tax_invoice' | 'challan' | 'quotation'
  invoiceNumber: '', invoiceDate: '', hsnCode: '8483', taxRate: 18, isInterState: false, paymentTerms: '30 Days Net',
  challanNumber: '', challanDate: '', challanType: 'against_sales_order', transportMode: 'Road',
  vehicleNumber: '', eWayBillNumber: '', lrNumber: '', receiverName: '', dispatchNotes: '',

  // Sales Logistics & Courier Details (Confidential to Sales — Hidden from Dispatch Dept)
  courierPartner: '', trackingNumber: '', shippingDate: '', estimatedDeliveryDate: '',
  shippingCharges: 0, freightTerms: '', logisticRemarks: '',

  ratePerMeter: 1200,
  discountPercent: 0,
  discountAmount: 0,
  grossAmount: 1200,
  totalAmount: 1200,
  gstAmount: 216,
  grandTotal: 1416,
  creditDays: 30,
  creditDueDate: '',
  paymentStatus: 'pending', paidAmount: 0, remarks: '', approvalStatus: 'none', changeReason: '',
  dragChainConfig: {
    modelNumber: '', width: '', radius: '', chainType: 'OP',
    pitch: 46, numberOfLinks: '22', totalCalculatedLinks: 22,
    length: '1000 MM', mountingBracketType: '', accessories: '',
    quantity: 1, ratePerMeter: 1200, discountPercent: 0, discountAmount: 0, grossAmount: 1200
  }
};

export default function SalesPipelineTab() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const subIndustryId = user?.organizationId?.subIndustryId || 'drag_chain';
  const isDragChain = subIndustryId === 'drag_chain';

  const [pipeline, setPipeline] = useState([]);
  const [stats, setStats] = useState({ leads: 0, quotations: 0, pis: 0, pos: 0, orders: 0, overdue: 0, pendingApproval: 0, approvedOrders: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalDocTab, setModalDocTab] = useState('tax_invoice'); // 'tax_invoice' | 'challan'
  const [productTemplates, setProductTemplates] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [approvingOrder, setApprovingOrder] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditRecord, setAuditRecord] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [followUpRecord, setFollowUpRecord] = useState(null);
  const [followUpNote, setFollowUpNote] = useState('');
  const [form, setForm] = useState(BLANK_FORM);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterApproval, setFilterApproval] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const isQuotation = form.stage === 'quotation' || modalDocTab === 'quotation';
  const isPI = form.stage === 'pi' || modalDocTab === 'pi';
  const isLead = form.stage === 'lead';

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStage) params.stage = filterStage;
      if (filterApproval) params.approvalStatus = filterApproval;
      if (filterDocType) params.orderDocumentType = filterDocType;
      const [pRes, sRes] = await Promise.all([
        salesPipelineAPI.getAll(params),
        salesPipelineAPI.getStats()
      ]);
      setPipeline(pRes.data.data);
      setStats(sRes.data.data);
    } catch {
      toast.error('Failed to load sales pipeline');
    } finally {
      setLoading(false);
    }
  }, [filterStage, filterApproval, filterDocType]);

  // Fetch product templates from DB for cascading dropdowns
  const fetchTemplates = useCallback(async () => {
    if (!isDragChain) return;
    try {
      const res = await operationsAPI.getProductTemplates();
      setProductTemplates(res.data.data || []);
    } catch {
      // silently fail — dropdowns will be empty
    }
  }, [isDragChain]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => {
    adminAPI.getUsers().then(res => {
      setTeamUsers(res.data?.data || []);
    }).catch(() => {
      // Non-admins might not have permission to view all users, handled gracefully
    });
  }, []);

  const STANDARD_WIDTHS = ['15', '25', '38', '50', '56', '75', '100', '125', '150', '175', '200', '225', '250', '300'];
  // Bending Radius: 5, 10, 15, 20 ... up to 700 (step of 5)
  const BENDING_RADII_OPTIONS = Array.from({ length: 140 }, (_, i) => String((i + 1) * 5));
  const STANDARD_LINKS = ['18', '20', '22', '24', '26', '28', '30'];

  // ---------- Cascading dropdown computed values ----------
  const catalogModelNumbers = useMemo(() => {
    const dbModels = productTemplates.map(t => t.modelNumber).filter(Boolean);
    return Array.from(new Set(dbModels)).sort();
  }, [productTemplates]);

  const availableWidths = useMemo(() => {
    let list = [];
    if (form.dragChainConfig.modelNumber) {
      list = productTemplates
        .filter(t => t.modelNumber === form.dragChainConfig.modelNumber)
        .map(t => t.width || t.innerSize)
        .filter(Boolean);
    }
    if (list.length === 0) {
      const allDbWidths = productTemplates.map(t => t.width || t.innerSize).filter(Boolean);
      list = Array.from(new Set([...allDbWidths, ...STANDARD_WIDTHS]));
    }
    return Array.from(new Set(list)).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      return (!isNaN(numA) && !isNaN(numB)) ? numA - numB : a.localeCompare(b);
    });
  }, [productTemplates, form.dragChainConfig.modelNumber]);

  const availableLinks = useMemo(() => {
    let links = [];
    if (form.dragChainConfig.modelNumber) {
      const matches = productTemplates.filter(t => t.modelNumber === form.dragChainConfig.modelNumber);
      links = Array.from(new Set(matches.map(t => t.numberOfLinks || t.linksPerMeter).filter(Boolean)));
    }
    if (links.length === 0) {
      const allDbLinks = productTemplates.map(t => t.numberOfLinks || t.linksPerMeter).filter(Boolean);
      links = Array.from(new Set([...allDbLinks, ...STANDARD_LINKS]));
    }
    return links.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  }, [productTemplates, form.dragChainConfig.modelNumber]);

  const availableRadii = useMemo(() => {
    let list = [];
    if (form.dragChainConfig.modelNumber) {
      const matches = productTemplates.filter(t => t.modelNumber === form.dragChainConfig.modelNumber);
      matches.forEach(t => {
        if (t.radius) {
          const parts = String(t.radius).split(/[,/ ]+/);
          parts.forEach(p => {
            const clean = p.replace(/^R/i, '').trim();
            if (clean && !isNaN(parseInt(clean, 10))) list.push(clean);
          });
        }
      });
    }
    if (list.length === 0) {
      list = BENDING_RADII_OPTIONS;
    }
    return Array.from(new Set(list)).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  }, [productTemplates, form.dragChainConfig.modelNumber]);

  // When model changes — reset Bending Radius & Width (Inner), cascade pitch, editable rate per meter (Database-First)
  const handleModelChange = (val) => {
    const spec = getSpecFromModel(val, productTemplates);

    const updated = recalculateAll({
      ...form,
      dragChainConfig: {
        ...form.dragChainConfig,
        modelNumber: val,
        // Client requirement: Bending Radius and Width (inner) values to be reset
        width: '',
        radius: '',
        chainType: form.dragChainConfig.chainType || 'OP',
        pitch: spec.pitch || 46,
        ratePerMeter: spec.ratePerMeter || 1200,
        mountingBracketType: form.dragChainConfig.mountingBracketType || '',
        accessories: form.dragChainConfig.accessories || ''
      }
    }, 'dragChainConfig.modelNumber', val, productTemplates);

    setForm(updated);
  };

  const openCreate = async (defaultStage = 'sales_order', defaultDocType = null) => {
    const year = new Date().getFullYear();
    const today = new Date().toISOString().split('T')[0];
    const rand = Math.floor(1000 + Math.random() * 9000);

    let nextSONum = `SO-${year}-${rand}`;
    let nextQuoteNum = `QTN-${year}-${rand}`;
    let nextPINum = `PI-${year}-${rand}`;
    try {
      const res = await salesPipelineAPI.getNextSONumber();
      if (res.data?.nextSONumber) {
        nextSONum = res.data.nextSONumber;
        nextQuoteNum = res.data.nextSONumber.replace('SO-', 'QTN-');
        nextPINum = res.data.nextSONumber.replace('SO-', 'PI-');
      }
    } catch (err) {
      console.warn('Could not fetch next SO number, using fallback:', err);
    }

    const isQuote = defaultStage === 'quotation';
    const isPi = defaultStage === 'pi';
    const isLd = defaultStage === 'lead';

    let initialDocType = 'tax_invoice';
    if (isQuote) initialDocType = 'quotation';
    else if (isPi) initialDocType = 'pi';
    else if (isLd) initialDocType = 'quotation';
    else if (defaultDocType) initialDocType = defaultDocType;

    const initial = recalculateAll({
      ...BLANK_FORM,
      stage: defaultStage,
      salesPersonName: user?.name || '',
      leadOwnerName: user?.name || '',
      leadOwner: user?._id || '',
      leadSource: 'Direct Inquiry',
      leadStatus: 'new',
      opportunityType: 'new_business',
      rating: 'warm',
      lifecycleStage: 'lead',
      salesOrderNumber: nextSONum,
      quotationNumber: (isPi || isLd) ? '' : nextQuoteNum,
      piNumber: nextPINum,
      customerPoNumber: '',
      quotationDate: today,
      invoiceNumber: (isQuote || isPi || isLd) ? '' : initialDocType === 'standard_bill' ? `BILL-${year}-${rand}` : `INV-${year}-${rand}`,
      challanNumber: `DC-${year}-${rand}`,
      invoiceDate: today,
      challanDate: today,
      taxRate: initialDocType === 'standard_bill' ? 0 : 18,
      orderDocumentType: initialDocType
    }, 'dragChainConfig.quantity', 1);

    setForm(initial);
    setModalDocTab(initialDocType);
    setEditingId(null);
    setShowModal(true);
  };

  const openAuditLogs = async (rec) => {
    setAuditRecord(rec);
    setShowAuditModal(true);
    setLoadingAudit(true);
    try {
      const res = await salesPipelineAPI.getAuditLogs(rec._id);
      if (res.data?.data) {
        setAuditRecord(res.data.data);
      }
    } catch (err) {
      console.warn('Could not fetch real-time audit logs, using record state:', err);
    } finally {
      setLoadingAudit(false);
    }
  };

  const openPreview = (rec) => {
    setPreviewRecord(rec);
    setShowPreviewModal(true);
  };
  
  const openEdit = (rec) => {
    const isQuote = rec.stage === 'quotation' || rec.orderDocumentType === 'quotation';
    const isPi = rec.stage === 'pi' || rec.orderDocumentType === 'pi';
    const isLd = rec.stage === 'lead';
    const docType = isQuote ? 'quotation' : isPi ? 'pi' : isLd ? 'quotation' : (rec.orderDocumentType || (rec.challanNumber ? 'challan' : 'tax_invoice'));
    const cfg = rec.items?.[0]?.dragChainConfig || rec.dragChainConfig || BLANK_FORM.dragChainConfig;
    const spec = getSpecFromModel(cfg.modelNumber || rec.modelNumber, productTemplates);

    const populated = {
      stage: rec.stage || 'sales_order',
      customerName: rec.customerName || '',
      salesPersonName: rec.salesPersonName || user?.name || '',
      customerContact: rec.customerContact || '',
      customerEmail: rec.customerEmail || '',
      customerAddress: rec.customerAddress || rec.billingAddress || '',
      customerGST: rec.customerGST || '',
      billingAddress: rec.billingAddress || rec.customerAddress || '',
      shippingAddress: rec.shippingAddress || rec.customerAddress || '',
      isSameAsBilling: rec.isSameAsBilling !== undefined ? !!rec.isSameAsBilling : (rec.billingAddress ? rec.billingAddress === rec.shippingAddress : true),
      quotationNumber: rec.quotationNumber || '',
      quotationDate: rec.quotationDate ? new Date(rec.quotationDate).toISOString().split('T')[0] : (rec.invoiceDate ? new Date(rec.invoiceDate).toISOString().split('T')[0] : ''),
      piNumber: rec.piNumber || '',
      customerPoNumber: rec.customerPoNumber || '',
      salesOrderNumber: rec.salesOrderNumber || '',

      // Salesforce Lead Fields
      leadOwner: rec.leadOwner?._id || rec.leadOwner || '',
      leadOwnerName: rec.leadOwnerName || rec.leadOwner?.name || rec.salesPersonName || user?.name || '',
      leadSource: rec.leadSource || 'Direct Inquiry',
      leadStatus: rec.leadStatus || 'new',
      opportunityType: rec.opportunityType || 'new_business',
      rating: rec.rating || 'warm',
      nextSteps: rec.nextSteps || '',
      nextFollowUpDate: rec.nextFollowUpDate ? new Date(rec.nextFollowUpDate).toISOString().split('T')[0] : '',
      rejectionCategory: rec.rejectionCategory || '',
      lifecycleStage: rec.lifecycleStage || 'lead',
      
      orderDocumentType: docType,
      invoiceNumber: rec.invoiceNumber || '',
      invoiceDate: rec.invoiceDate ? new Date(rec.invoiceDate).toISOString().split('T')[0] : '',
      hsnCode: rec.hsnCode || '8483',
      taxRate: rec.taxRate ?? 18,
      isInterState: !!rec.isInterState,
      paymentTerms: rec.paymentTerms || '30 Days Net',

      challanNumber: rec.challanNumber || '',
      challanDate: rec.challanDate ? new Date(rec.challanDate).toISOString().split('T')[0] : '',
      challanType: rec.challanType || 'against_sales_order',
      transportMode: rec.transportMode || 'Road',
      vehicleNumber: rec.vehicleNumber || '',
      eWayBillNumber: rec.eWayBillNumber || '',
      lrNumber: rec.lrNumber || '',
      receiverName: rec.receiverName || '',
      dispatchNotes: rec.dispatchNotes || '',

      // Sales Logistics & Courier Details
      courierPartner: rec.courierPartner || '',
      trackingNumber: rec.trackingNumber || '',
      shippingDate: rec.shippingDate ? new Date(rec.shippingDate).toISOString().split('T')[0] : '',
      estimatedDeliveryDate: rec.estimatedDeliveryDate ? new Date(rec.estimatedDeliveryDate).toISOString().split('T')[0] : '',
      shippingCharges: rec.shippingCharges || 0,
      freightTerms: rec.freightTerms || '',
      logisticRemarks: rec.logisticRemarks || '',

      ratePerMeter: rec.ratePerMeter || cfg.ratePerMeter || spec.ratePerMeter,
      discountPercent: rec.discountPercent ?? cfg.discountPercent ?? 0,
      discountAmount: rec.discountAmount || cfg.discountAmount || 0,
      grossAmount: rec.grossAmount || cfg.grossAmount || 0,
      totalAmount: rec.totalAmount || 0,
      gstAmount: rec.gstAmount || 0,
      grandTotal: rec.grandTotal || 0,
      creditDays: rec.creditDays || 30,
      creditDueDate: rec.creditDueDate ? new Date(rec.creditDueDate).toISOString().split('T')[0] : '',
      paymentStatus: rec.paymentStatus || 'pending',
      paidAmount: rec.paidAmount || 0,
      remarks: rec.remarks || '',
      changeReason: '',
      approvalStatus: rec.approvalStatus || 'none',
      dragChainConfig: {
        ...cfg,
        pitch: cfg.pitch || spec.pitch || 46,
        ratePerMeter: cfg.ratePerMeter || spec.ratePerMeter,
        discountPercent: cfg.discountPercent ?? rec.discountPercent ?? 0,
        totalCalculatedLinks: cfg.totalCalculatedLinks || Math.ceil(((parseFloat(cfg.quantity) || 1) * 1000) / (cfg.pitch || spec.pitch || 46))
      }
    };

    const calculated = recalculateAll(populated);
    setForm(calculated);
    setModalDocTab(docType);
    setEditingId(rec._id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const isQuotation = form.stage === 'quotation' || modalDocTab === 'quotation';
    const isPI = form.stage === 'pi' || modalDocTab === 'pi';
    const isLeadStage = form.stage === 'lead';
    try {
      const payload = {
        ...form,
        orderDocumentType: isQuotation ? 'quotation' : isPI ? 'pi' : isLeadStage ? 'quotation' : modalDocTab,
        auditNote: form.changeReason || '',
        items: [{
          modelNumber: form.dragChainConfig.modelNumber || 'Custom Drag Chain',
          quantity: form.dragChainConfig.quantity || 1,
          unit: isDragChain ? 'meter' : 'pcs',
          unitPrice: form.dragChainConfig.ratePerMeter || form.ratePerMeter || 1200,
          totalPrice: form.grandTotal,
          dragChainConfig: form.dragChainConfig
        }]
      };

      if (editingId) {
        await salesPipelineAPI.update(editingId, payload);
        toast.success(
          isLeadStage
            ? '✅ Salesforce Lead updated successfully'
            : isQuotation 
            ? '✅ Quotation updated successfully' 
            : isPI 
            ? '✅ Proforma Invoice (PI) updated successfully' 
            : form.orderDocumentType === 'standard_bill' || modalDocTab === 'standard_bill'
            ? '✅ Standard Bill (Non-GST) updated successfully'
            : 'Record updated & changes logged in Audit Trail'
        );
      } else {
        await salesPipelineAPI.create(payload);
        toast.success(
          isLeadStage
            ? '✅ Salesforce Lead Created Successfully'
            : isQuotation 
            ? '✅ Quotation Created Successfully' 
            : isPI 
            ? '✅ Proforma Invoice (PI) Created Successfully' 
            : modalDocTab === 'standard_bill'
            ? '✅ Standard Bill (Non-GST) Created Successfully'
            : modalDocTab === 'challan' 
            ? '✅ Delivery Challan Sales Order Created' 
            : '✅ Tax Invoice Sales Order Created'
        );
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pipeline record?')) return;
    try {
      await salesPipelineAPI.delete(id);
      toast.success('Record deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleStageAdvance = async (id, stage) => {
    try {
      await salesPipelineAPI.update(id, { stage });
      toast.success(`Advanced to ${STAGE_CONFIG[stage].label}`);
      fetchData();
    } catch {
      toast.error('Failed to advance stage');
    }
  };

  // Admin picks Credit / Advance in the modal; approval releases the order to the store
  const handleApproveOrder = (rec) => setApprovingOrder(rec);

  const handleRejectOrder = async (id) => {
    const reason = window.prompt('Enter reason for rejecting this Sales Order (sent to Sales team):');
    if (reason === null) return; // User cancelled
    try {
      await salesPipelineAPI.reject(id, reason.trim() || 'Pricing or Terms not approved');
      toast.error('❌ Sales Order Rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject Sales Order');
    }
  };

  const handleAddFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpNote.trim()) return;
    try {
      await salesPipelineAPI.addFollowUp(followUpRecord._id, followUpNote);
      toast.success('Follow-up note added');
      setShowFollowUpModal(false);
      setFollowUpNote('');
      fetchData();
    } catch {
      toast.error('Failed to add follow-up');
    }
  };

  // Helper for total calculation
  const handleAmountChange = (val, isGSTRate = false) => {
    let amt = form.totalAmount;
    if (!isGSTRate) {
      amt = Number(val);
      const gst = amt * 0.18;
      setForm(p => ({ ...p, totalAmount: amt, gstAmount: gst, grandTotal: amt + gst }));
    }
  };

  const isOverdue = (rec) => {
    if (rec.paymentStatus === 'paid') return false;
    if (rec.paymentStatus === 'overdue') return true;
    if (rec.creditDueDate && new Date(rec.creditDueDate) < new Date()) return true;
    return false;
  };

  const filtered = pipeline.filter(p =>
    p.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    p.leadOwnerName?.toLowerCase().includes(search.toLowerCase()) ||
    p.salesPersonName?.toLowerCase().includes(search.toLowerCase()) ||
    p.quotationNumber?.toLowerCase().includes(search.toLowerCase()) ||
    p.piNumber?.toLowerCase().includes(search.toLowerCase()) ||
    p.customerPoNumber?.toLowerCase().includes(search.toLowerCase()) ||
    p.salesOrderNumber?.toLowerCase().includes(search.toLowerCase()) ||
    p.invoiceNumber?.toLowerCase().includes(search.toLowerCase()) ||
    p.challanNumber?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header / Sub-nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            🎯 Sales Pipeline & {isDragChain ? 'Drag Chain ' : ''}Order Fulfillment Funnel
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            Seamless Lifecycle: Sales (Lead/PI) ➔ Confirmed Order ➔ (Store) Assembly ➔ Box Packing ➔ Logistics Dispatch
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => openCreate('lead')} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '5px', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}>
            <HiOutlinePlus /> 🎯 Lead
          </button>
          <button onClick={() => openCreate('sales_order', 'tax_invoice')} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', fontWeight: 700 }}>
            <HiOutlinePlus /> 🧾 Tax Invoice (GST)
          </button>
          <button onClick={() => openCreate('sales_order', 'standard_bill')} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', fontWeight: 700 }}>
            <HiOutlinePlus /> 📄 Standard Bill (Non-GST)
          </button>
          <button onClick={() => openCreate('sales_order', 'challan')} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', fontWeight: 700 }}>
            <HiOutlinePlus /> 🚚 Delivery Challan
          </button>
          <button onClick={() => openCreate('pi')} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '5px', borderColor: '#f59e0b', color: '#d97706', fontWeight: 600 }}>
            <HiOutlinePlus /> 📑 Proforma Inv (PI)
          </button>
          <button onClick={() => openCreate('quotation')} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '5px', borderColor: '#3b82f6', color: '#3b82f6', fontWeight: 600 }}>
            <HiOutlinePlus /> 📜 Quotation
          </button>
        </div>
      </div>

      {/* End-to-End 9-Step Lifecycle Banner */}
      <OrderWorkflowBanner
        activeStageKey={filterStage || 'lead'}
        onStepClick={(s) => {
          if (['lead', 'quotation', 'customer_po', 'pi', 'sales_order'].includes(s.key)) {
            setFilterStage(s.key);
            setFilterApproval('');
          } else if (s.key === 'invoice') {
            setFilterStage('sales_order');
            setFilterDocType('tax_invoice');
            setFilterApproval('');
          } else if (s.key === 'payment') {
            setFilterStage('sales_order');
          }
        }}
      />

      {/* Funnel Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        {Object.entries(STAGE_CONFIG).map(([key, cfg]) => (
          <div key={key} onClick={() => { setFilterStage(filterStage === key ? '' : key); setFilterApproval(''); }}
            style={{
              background: filterStage === key ? cfg.bg : 'var(--bg-card)',
              border: `1px solid ${filterStage === key ? cfg.color : 'var(--border)'}`,
              borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s'
            }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>{cfg.icon}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: cfg.color }}>
              {key === 'lead' ? stats.leads : key === 'quotation' ? stats.quotations : key === 'pi' ? stats.pis : key === 'customer_po' ? stats.pos : stats.orders}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', fontWeight: 600 }}>{cfg.label}</div>
          </div>
        ))}

        {/* Pending Admin Approval Card */}
        <div onClick={() => { setFilterApproval(filterApproval === 'pending' ? '' : 'pending'); setFilterStage('sales_order'); }}
          style={{
            background: filterApproval === 'pending' ? 'rgba(234, 179, 8, 0.25)' : 'var(--bg-card)',
            border: `1px solid ${filterApproval === 'pending' ? '#eab308' : (stats.pendingApproval > 0 ? 'rgba(234,179,8,0.5)' : 'var(--border)')}`,
            borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s'
          }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>⏳</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#eab308' }}>{stats.pendingApproval || 0}</div>
          <div style={{ fontSize: '0.8rem', color: '#eab308', fontWeight: 700 }}>SO Pending Approval</div>
        </div>

        {/* Overdue Payment Card */}
        <div style={{
          background: stats.overdue > 0 ? 'rgba(239,68,68,0.15)' : 'var(--bg-card)',
          border: `1px solid ${stats.overdue > 0 ? '#ef4444' : 'var(--border)'}`,
          borderRadius: '12px', padding: '16px'
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🔴</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ef4444' }}>{stats.overdue}</div>
          <div style={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 700 }}>Payment Overdue</div>
        </div>
      </div>

      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input className="form-input" style={{ paddingLeft: '36px' }} placeholder="Search customer, PI #, PO #, quote #, SO #, Inv #, Challan #..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" style={{ width: '180px' }} value={filterStage} onChange={e => setFilterStage(e.target.value)}>
          <option value="">All Stages</option>
          {Object.entries(STAGE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="form-input" style={{ width: '205px' }} value={filterDocType} onChange={e => setFilterDocType(e.target.value)}>
          <option value="">All Document Types</option>
          <option value="tax_invoice">🧾 Tax Invoice (GST)</option>
          <option value="standard_bill">📄 Standard Bill (Non-GST)</option>
          <option value="challan">🚚 Delivery Challan (DC)</option>
          <option value="quotation">📜 Quotations</option>
          <option value="pi">📑 Proforma Invoices (PI)</option>
        </select>
        <select className="form-input" style={{ width: '190px' }} value={filterApproval} onChange={e => setFilterApproval(e.target.value)}>
          <option value="">All Approval Statuses</option>
          <option value="pending">⏳ Pending Approval</option>
          <option value="approved">✅ Approved</option>
          <option value="rejected">❌ Rejected</option>
        </select>
      </div>

      {/* Pipeline Table — ID, Customer, Stage, Date, Actions. Full details open in the View modal. */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-card-hover)' }}>
                {['ID', 'Customer Name', 'Stage', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: '0.76rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>Loading pipeline...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>No pipeline records found</td></tr>
              ) : filtered.map(rec => {
                const sc = STAGE_CONFIG[rec.stage] || STAGE_CONFIG.lead;
                const approval = getApproval(rec);
                const overdue = isOverdue(rec);
                const canDecide = isAdmin && rec.stage === 'sales_order' && rec.approvalStatus !== 'approved';

                return (
                  <tr key={rec._id} onClick={() => setViewRecord(rec)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 800, color: 'var(--primary)', fontFamily: 'ui-monospace, Consolas, monospace' }}>{getRecordNumber(rec)}</div>
                      {DOC_TYPE_LABELS[rec.orderDocumentType] && rec.stage === 'sales_order' && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>{DOC_TYPE_LABELS[rec.orderDocumentType]}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{rec.customerName}</div>
                      {rec.customerContact && <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px' }}>📞 {rec.customerContact}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: sc.bg, color: sc.color, border: `1px solid ${sc.color}55`, borderRadius: '999px', padding: '3px 10px', fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {sc.icon} {sc.label}
                      </span>
                      {approval && (
                        <div style={{ marginTop: '5px', fontSize: '0.72rem', fontWeight: 700, color: approval.color, whiteSpace: 'nowrap' }}>
                          {approval.icon} {approval.label}
                          {rec.approvalStatus === 'approved' && rec.dispatchId?.status && (
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}> · {STORE_STATUS_LABELS[rec.dispatchId.status] || humanize(rec.dispatchId.status)}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmtDate(rec.createdAt)}</div>
                      {overdue && <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 800, marginTop: '2px' }}>🚨 Payment Overdue</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {canDecide && (
                          <>
                            <ActionButton icon={<HiOutlineCheckCircle />} label="Approve" color="#16a34a" onClick={() => handleApproveOrder(rec)} title="Approve & release to store" />
                            {rec.approvalStatus !== 'rejected' && (
                              <ActionButton icon={<HiOutlineXCircle />} label="Reject" color="#dc2626" onClick={() => handleRejectOrder(rec._id)} title="Reject Sales Order" />
                            )}
                          </>
                        )}
                        <ActionButton icon={<HiOutlineEye />} label="View" color="#2563eb" onClick={() => setViewRecord(rec)} title="View full details" />
                        <ActionButton icon={<HiOutlinePencil />} label="Edit" color="#475569" onClick={() => openEdit(rec)} />
                        <ActionButton icon={<HiOutlineTrash />} label="Delete" color="#dc2626" onClick={() => handleDelete(rec._id)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* VIEW FULL DETAILS MODAL */}
      {viewRecord && (() => {
        const rec = viewRecord;
        const sc = STAGE_CONFIG[rec.stage] || STAGE_CONFIG.lead;
        const approval = getApproval(rec);
        const cfg = rec.items?.[0]?.dragChainConfig || rec.dragChainConfig || {};
        const isSO = rec.stage === 'sales_order';
        const isNonGst = ['standard_bill', 'non_gst_bill'].includes(rec.orderDocumentType);
        const released = getReleasedBy(rec);
        const overdue = isOverdue(rec);
        const closeAnd = (fn) => () => { setViewRecord(null); fn(); };
        const nextStep = {
          lead: { label: '📜 Convert to Quotation', run: () => openEdit({ ...rec, stage: 'quotation', orderDocumentType: 'quotation' }) },
          quotation: { label: '📑 Customer PO Received', run: () => handleStageAdvance(rec._id, 'customer_po') },
          customer_po: { label: '🧾 Issue PI', run: () => handleStageAdvance(rec._id, 'pi') },
          pi: { label: '🏷️ Convert to Sales Order', run: () => handleStageAdvance(rec._id, 'sales_order') }
        }[rec.stage];

        const sections = [
          {
            title: 'Record Summary', icon: '📋', rows: [
              { label: 'Record ID', value: getRecordNumber(rec) },
              { label: 'Stage', value: `${sc.icon} ${sc.label}` },
              { label: 'Document Type', value: DOC_TYPE_LABELS[rec.orderDocumentType] },
              { label: 'Created On', value: formatDateTime(rec.createdAt) },
              { label: 'Last Updated', value: formatDateTime(rec.updatedAt) },
              { label: 'Released By', value: isSO && released.name ? `${released.name}${released.role ? ` (${formatRole(released.role)})` : ''}` : '', hint: 'Who raised this Sales Order' },
              { label: 'Admin Approval', value: approval ? `${approval.icon} ${approval.label}` : '' },
              { label: 'Approved By', value: rec.approvalStatus === 'approved' ? (rec.approvedBy?.name || '') : '' },
              { label: 'Approval Date', value: formatDateTime(rec.approvalDate) },
              { label: 'Payment Basis', value: formatPaymentBasis(rec), hint: 'Credit days or advance, set by Admin' },
              { label: 'Approval Note', value: rec.approvalNote },
              { label: 'Rejection / Lost Reason', value: rec.rejectionReason },
              { label: 'Store Status', value: isSO && rec.approvalStatus === 'approved' ? (STORE_STATUS_LABELS[rec.dispatchId?.status] || 'Queued for store') : '', hint: 'Live position of the order in the store' }
            ]
          },
          {
            title: 'Customer Details', icon: '🏢', rows: [
              { label: 'Customer Name', value: rec.customerName },
              { label: 'Contact Number', value: rec.customerContact },
              { label: 'Email', value: rec.customerEmail },
              { label: 'GSTIN', value: rec.customerGST, hint: 'Customer GST number' },
              { label: 'Billing Address', value: rec.billingAddress || rec.customerAddress },
              { label: 'Shipping Address', value: rec.isSameAsBilling && !rec.shippingAddress ? 'Same as billing address' : (rec.shippingAddress || rec.customerAddress) }
            ]
          },
          {
            title: 'Sales Owner & Lead', icon: '👤', rows: [
              { label: 'Sales Person', value: rec.salesPersonName },
              { label: 'Lead Owner', value: rec.leadOwnerName || rec.leadOwner?.name },
              { label: 'Lead Source', value: rec.leadSource },
              { label: 'Lead Status', value: LEAD_STATUS_OPTIONS.find(s => s.value === rec.leadStatus)?.label || humanize(rec.leadStatus) },
              { label: 'Opportunity Type', value: OPPORTUNITY_TYPE_OPTIONS.find(o => o.value === rec.opportunityType)?.label || humanize(rec.opportunityType) },
              { label: 'Rating', value: RATING_OPTIONS.find(r => r.value === rec.rating) ? `${RATING_OPTIONS.find(r => r.value === rec.rating).icon} ${RATING_OPTIONS.find(r => r.value === rec.rating).label}` : '', hint: 'Hot / Warm / Cold buying intent' },
              { label: 'Lifecycle Stage', value: LIFECYCLE_STAGES.find(l => l.key === rec.lifecycleStage)?.label || humanize(rec.lifecycleStage) },
              { label: 'Next Steps', value: rec.nextSteps },
              { label: 'Next Follow-up Date', value: fmtDate(rec.nextFollowUpDate) },
              { label: 'Lost Reason Category', value: REJECTION_REASONS.find(r => r.value === rec.rejectionCategory)?.label }
            ]
          },
          {
            title: 'Document Numbers & Dates', icon: '🧾', rows: [
              { label: 'Quotation No.', value: rec.quotationNumber },
              { label: 'Quotation Date', value: fmtDate(rec.quotationDate) },
              { label: 'Customer PO No.', value: rec.customerPoNumber },
              { label: 'Proforma Invoice (PI) No.', value: rec.piNumber },
              { label: 'Sales Order No.', value: rec.salesOrderNumber },
              { label: isNonGst ? 'Bill No.' : 'Invoice No.', value: rec.invoiceNumber },
              { label: isNonGst ? 'Bill Date' : 'Invoice Date', value: fmtDate(rec.invoiceDate) },
              { label: 'Delivery Challan No.', value: rec.challanNumber },
              { label: 'Challan Date', value: fmtDate(rec.challanDate) },
              { label: 'Challan Type', value: rec.orderDocumentType === 'challan' ? humanize(rec.challanType) : '' },
              { label: 'HSN Code', value: rec.hsnCode, hint: 'Tax classification code of the product' },
              { label: 'Payment Terms', value: rec.paymentTerms }
            ]
          },
          isDragChain ? {
            title: 'Drag Chain Specification', icon: '⛓️', rows: [
              { label: 'Model / Series', value: cfg.modelNumber || cfg.series },
              { label: 'Order Code', value: (cfg.modelNumber || cfg.series) ? formatDragChainOrderCode(cfg) : '', hint: 'Full product code for production' },
              { label: 'Chain Type', value: CHAIN_TYPE_LABELS[cfg.chainType] || cfg.chainType },
              { label: 'Inner Width', value: cfg.width ? `${cfg.width} mm` : '' },
              { label: 'Bending Radius', value: cfg.radius ? `R${String(cfg.radius).replace(/^R/i, '')} mm` : '', hint: 'Minimum radius the chain bends at' },
              { label: 'Pitch', value: cfg.pitch ? `${cfg.pitch} mm` : '', hint: 'Length of one link' },
              { label: 'Length per Set', value: cfg.length },
              { label: 'No. of Sets', value: cfg.quantity, hint: 'How many chains the customer needs' },
              { label: 'Total Meters', value: cfg.totalMeters ? `${cfg.totalMeters} m` : '' },
              { label: 'Links per Set', value: cfg.linksPerSet },
              { label: 'Total Links', value: cfg.totalCalculatedLinks || cfg.numberOfLinks },
              { label: 'Mounting Bracket', value: cfg.mountingBracketType ? `${cfg.mountingBracketType} (${formatMountingBracketCode(cfg.mountingBracketType, cfg.modelNumber || '')})` : '' },
              { label: 'Accessories', value: cfg.accessories }
            ]
          } : {
            title: 'Products', icon: '📦', wide: true, table: {
              columns: ['Model / Item', 'Qty', 'Unit', 'Unit Price', 'Total'],
              rows: (rec.items || []).map(it => [it.modelNumber || it.description, it.quantity, it.unit, fmtMoney(it.unitPrice), fmtMoney(it.totalPrice)])
            }
          },
          {
            title: 'Pricing & Tax', icon: '💰', rows: [
              { label: isDragChain ? 'Rate per Meter' : 'Rate', value: fmtMoney(rec.ratePerMeter || cfg.ratePerMeter) },
              { label: 'Gross Amount', value: fmtMoney(rec.grossAmount || cfg.grossAmount) },
              { label: 'Discount %', value: `${rec.discountPercent ?? cfg.discountPercent ?? 0}%` },
              { label: 'Discount Amount', value: fmtMoney(rec.discountAmount || cfg.discountAmount || 0) },
              { label: 'Taxable Amount', value: fmtMoney(rec.totalAmount), hint: 'Gross amount minus discount' },
              { label: 'GST Rate', value: isNonGst ? 'Not applicable (Non-GST)' : `${rec.taxRate ?? 18}%` },
              { label: 'GST Type', value: isNonGst ? '' : (rec.isInterState ? 'IGST (Inter-state)' : 'CGST + SGST (Intra-state)') },
              { label: 'GST Amount', value: fmtMoney(rec.gstAmount || 0) },
              { label: 'Shipping Charges', value: fmtMoney(rec.shippingCharges || 0) },
              { label: 'Grand Total', value: <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--primary)' }}>{fmtMoney(rec.grandTotal || rec.totalAmount || 0)}</span> }
            ]
          },
          {
            title: 'Payment & Credit', icon: '💳', rows: [
              { label: 'Payment Status', value: humanize(rec.paymentStatus) },
              { label: 'Credit Days', value: rec.creditDays ? `${rec.creditDays} days` : '' },
              { label: 'Credit Due Date', value: rec.creditDueDate ? `${fmtDate(rec.creditDueDate)}${overdue ? '  🚨 OVERDUE' : ''}` : '' },
              { label: 'Paid Amount', value: fmtMoney(rec.paidAmount || 0) },
              { label: 'Balance Due', value: fmtMoney(Math.max(0, (rec.grandTotal || 0) - (rec.paidAmount || 0))) }
            ]
          },
          {
            title: 'Logistics & Delivery', icon: '🚚', rows: [
              { label: 'Transport Mode', value: rec.transportMode },
              { label: 'Vehicle No.', value: rec.vehicleNumber },
              { label: 'E-Way Bill No.', value: rec.eWayBillNumber },
              { label: 'LR No.', value: rec.lrNumber || rec.dispatchId?.lrNumber, hint: 'Lorry Receipt from transporter' },
              { label: 'Receiver Name', value: rec.receiverName },
              { label: 'Courier Partner', value: rec.courierPartner },
              { label: 'Tracking No.', value: rec.trackingNumber },
              { label: 'Shipping Date', value: fmtDate(rec.shippingDate) },
              { label: 'Estimated Delivery', value: fmtDate(rec.estimatedDeliveryDate) },
              { label: 'Freight Terms', value: humanize(rec.freightTerms) },
              { label: 'Logistics Remarks', value: rec.logisticRemarks },
              { label: 'Dispatch Notes', value: rec.dispatchNotes }
            ]
          },
          {
            title: 'Remarks', icon: '📝', rows: [
              { label: 'Remarks', value: rec.remarks },
              { label: 'Follow-up Notes', value: `${rec.followUps?.length || 0}` },
              { label: 'Audit Log Entries', value: `${rec.auditLogs?.length || 0}` }
            ]
          },
          {
            title: 'Follow-up History', icon: '💬', wide: true, table: {
              columns: ['Date', 'Note'],
              rows: (rec.followUps || []).map(f => [formatDateTime(f.date), f.note])
            }
          }
        ];

        return (
          <RecordDetailsModal
            title={`${getRecordNumber(rec)} · ${rec.customerName}`}
            subtitle={DOC_TYPE_LABELS[rec.orderDocumentType] || sc.label}
            badges={[
              { label: `${sc.icon} ${sc.label}`, color: sc.color, bg: sc.bg },
              approval && { label: `${approval.icon} ${approval.label}`, color: approval.color, bg: approval.bg },
              overdue && { label: '🚨 Payment Overdue', color: '#dc2626', bg: 'rgba(239,68,68,0.12)' }
            ]}
            sections={sections}
            onClose={() => setViewRecord(null)}
            actions={
              <>
                {nextStep && (
                  <button className="btn btn-secondary" onClick={closeAnd(nextStep.run)} style={{ fontWeight: 700 }}>{nextStep.label}</button>
                )}
                <button className="btn btn-secondary" onClick={closeAnd(() => { setFollowUpRecord(rec); setShowFollowUpModal(true); })} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <HiOutlineChatAlt2 /> Follow-ups ({rec.followUps?.length || 0})
                </button>
                <button className="btn btn-secondary" onClick={closeAnd(() => openAuditLogs(rec))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <HiOutlineClipboardList /> Audit Logs ({rec.auditLogs?.length || 0})
                </button>
                <button className="btn btn-secondary" onClick={closeAnd(() => openPreview(rec))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <HiOutlinePrinter /> Print
                </button>
                {isAdmin && isSO && rec.approvalStatus !== 'approved' && (
                  <>
                    {rec.approvalStatus !== 'rejected' && (
                      <button className="btn btn-danger" onClick={closeAnd(() => handleRejectOrder(rec._id))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <HiOutlineXCircle /> Reject
                      </button>
                    )}
                    <button className="btn btn-primary" onClick={closeAnd(() => handleApproveOrder(rec))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#16a34a', borderColor: '#16a34a' }}>
                      <HiOutlineCheckCircle /> Approve
                    </button>
                  </>
                )}
                <button className="btn btn-primary" onClick={closeAnd(() => openEdit(rec))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <HiOutlinePencil /> Edit
                </button>
              </>
            }
          />
        );
      })()}

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '820px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            border: '1px solid var(--border)', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Fixed Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>
                  {editingId 
                    ? (isLead ? '✏️ Edit Salesforce Lead' : isQuotation ? '✏️ Edit Sales Quotation' : isPI ? '✏️ Edit Proforma Invoice (PI)' : '✏️ Edit Sales Pipeline Record') 
                    : (isLead ? '🎯 Create Salesforce Lead' : isQuotation ? '📜 Create Sales Quotation' : isPI ? '📄 Create Proforma Invoice (PI)' : '🚀 Create Sales Pipeline Record')}
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                  {isLead
                    ? 'Salesforce Lead Qualification & Opportunity Module: Lead Owner, Qualification, Rating, Next Steps, and Lifecycle Stage.'
                    : isQuotation 
                    ? 'Customer price proposal & quotation estimate form.' 
                    : isPI
                    ? 'Commercial Proforma Invoice for customer advance payment with Customer PO reference.'
                    : 'Drag Chain specification form with automated pitch & link calculation, fixed base price & discount, and auto SO numbering.'}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingId && (
                  <button
                    type="button"
                    onClick={() => openAuditLogs({ _id: editingId, customerName: form.customerName, salesOrderNumber: form.salesOrderNumber, stage: form.stage, approvalStatus: form.approvalStatus })}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <HiOutlineClipboardList /> View Audit History
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  title="Close"
                  style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
                    color: 'var(--text-dim)', cursor: 'pointer', fontSize: '1.2rem',
                    borderRadius: '8px', width: '32px', height: '32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <HiOutlineX />
                </button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
              {/* DOCUMENT TYPE TABS (Tax Invoice vs Delivery Challan) - Hidden for Lead, Quotation & PI */}
            {isLead ? (
              <div style={{ padding: '14px 18px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.3rem' }}>🎯</span>
                    <div>
                      <div style={{ fontWeight: 700, color: '#6366f1', fontSize: '0.92rem' }}>Salesforce Lead Qualification Module</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>Manage Lead Ownership, Qualification, Opportunity Type, Rating & Lifecycle Stages.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{
                      fontSize: '0.74rem',
                      background: form.rating === 'hot' ? 'rgba(239,68,68,0.2)' : form.rating === 'cold' ? 'rgba(59,130,246,0.2)' : 'rgba(245,158,11,0.2)',
                      color: form.rating === 'hot' ? '#ef4444' : form.rating === 'cold' ? '#3b82f6' : '#d97706',
                      padding: '3px 8px', borderRadius: '6px', fontWeight: 800, border: `1px solid ${form.rating === 'hot' ? 'rgba(239,68,68,0.4)' : form.rating === 'cold' ? 'rgba(59,130,246,0.4)' : 'rgba(245,158,11,0.4)'}`
                    }}>
                      {form.rating === 'hot' ? '🔥 Hot Lead' : form.rating === 'cold' ? '❄️ Cold Lead' : '🌤️ Warm Lead'}
                    </span>
                  </div>
                </div>

                {/* Lifecycle Stage Progression Bar */}
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-dim)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>LIFECYCLE STAGE PROGRESSION:</span>
                    <span style={{ color: '#6366f1' }}>Current: {LIFECYCLE_STAGES.find(s => s.key === form.lifecycleStage)?.label || 'Lead'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', background: 'rgba(0,0,0,0.15)', padding: '4px', borderRadius: '8px', overflowX: 'auto' }}>
                    {LIFECYCLE_STAGES.map((st) => {
                      const isActive = (form.lifecycleStage || 'lead') === st.key;
                      return (
                        <button
                          key={st.key}
                          type="button"
                          onClick={() => setForm(p => ({ ...p, lifecycleStage: st.key }))}
                          style={{
                            flex: 1, minWidth: '95px', padding: '6px 8px', borderRadius: '6px', border: 'none',
                            background: isActive ? '#6366f1' : 'transparent',
                            color: isActive ? '#fff' : 'var(--text-dim)',
                            fontSize: '0.76rem', fontWeight: isActive ? 700 : 500,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                            transition: 'all 0.15s'
                          }}
                        >
                          <span>{st.icon}</span>
                          <span>{st.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : isQuotation ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '10px', marginBottom: '16px' }}>
                <span style={{ fontSize: '1.3rem' }}>📜</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: '0.9rem' }}>Customer Quotation Proposal</div>
                  <div style={{ fontSize: '0.73rem', color: 'var(--text-dim)' }}>Price proposal & estimate for customer inquiry. Tax Invoice, GST supply & dispatch courier details are excluded for Quotation.</div>
                </div>
              </div>
            ) : isPI ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '10px', marginBottom: '16px' }}>
                <span style={{ fontSize: '1.3rem' }}>📄</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#d97706', fontSize: '0.9rem' }}>Commercial Proforma Invoice (PI)</div>
                  <div style={{ fontSize: '0.73rem', color: 'var(--text-dim)' }}>Proforma invoice for customer advance payment. Delivery Challan & dispatch logistics details are excluded for PI.</div>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginBottom: '16px',
                background: 'rgba(255,255,255,0.03)',
                padding: '6px',
                borderRadius: '12px',
                border: '1px solid var(--border)'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setModalDocTab('tax_invoice');
                    const updated = recalculateAll({
                      ...form,
                      orderDocumentType: 'tax_invoice',
                      taxRate: form.taxRate || 18,
                      invoiceNumber: form.invoiceNumber && !form.invoiceNumber.startsWith('DC-') ? form.invoiceNumber : `INV-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
                    }, 'taxRate', form.taxRate || 18, productTemplates);
                    setForm(updated);
                  }}
                  style={{
                    padding: '10px 12px', borderRadius: '10px',
                    border: modalDocTab === 'tax_invoice' ? '2px solid #3b82f6' : '1px solid transparent',
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem',
                    transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                    background: modalDocTab === 'tax_invoice' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                    color: modalDocTab === 'tax_invoice' ? '#3b82f6' : 'var(--text-dim)'
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>🧾</span>
                  <span>Tax Invoice (GST)</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.85 }}>Official GSTIN / 18% Tax</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setModalDocTab('standard_bill');
                    const updated = recalculateAll({
                      ...form,
                      orderDocumentType: 'standard_bill',
                      taxRate: 0,
                      gstAmount: 0,
                      invoiceNumber: form.invoiceNumber && !form.invoiceNumber.startsWith('DC-') ? form.invoiceNumber : `BILL-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
                    }, 'orderDocumentType', 'standard_bill', productTemplates);
                    setForm(updated);
                  }}
                  style={{
                    padding: '10px 12px', borderRadius: '10px',
                    border: modalDocTab === 'standard_bill' ? '2px solid #10b981' : '1px solid transparent',
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem',
                    transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                    background: modalDocTab === 'standard_bill' ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                    color: modalDocTab === 'standard_bill' ? '#10b981' : 'var(--text-dim)'
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>📄</span>
                  <span>Standard Bill (Non-GST)</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.85 }}>0% GST / Bill of Supply</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setModalDocTab('challan');
                    const updated = recalculateAll({
                      ...form,
                      orderDocumentType: 'challan',
                      challanNumber: form.challanNumber || `DC-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
                    }, 'orderDocumentType', 'challan', productTemplates);
                    setForm(updated);
                  }}
                  style={{
                    padding: '10px 12px', borderRadius: '10px',
                    border: modalDocTab === 'challan' ? '2px solid #8b5cf6' : '1px solid transparent',
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem',
                    transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                    background: modalDocTab === 'challan' ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                    color: modalDocTab === 'challan' ? '#8b5cf6' : 'var(--text-dim)'
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>🚚</span>
                  <span>Delivery Challan (DC)</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.85 }}>Goods Dispatch & Vehicle No</span>
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Post-Approval Editing Notice Banner */}
              {form.approvalStatus === 'approved' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                  background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)',
                  borderRadius: '8px', marginBottom: '14px', fontSize: '0.82rem', color: '#3b82f6'
                }}>
                  <HiOutlineShieldCheck style={{ fontSize: '1.4rem', flexShrink: 0 }} />
                  <div>
                    <strong>Admin Approved Order:</strong> {isAdmin
                      ? 'You can edit this order; it stays approved.'
                      : 'Saving changes sends this order back for Admin re-approval and puts it on hold in the store until Admin approves it again.'} All modifications are permanently recorded in the <strong>Audit Log Trail</strong> with your user identity and timestamp.
                  </div>
                </div>
              )}

              {/* If isLead: render full Salesforce Module. Otherwise render standard SO/Quote/PI fields */}
              {isLead ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                  {/* Pipeline Stage Selector for Leads */}
                  <div style={{ padding: '12px 16px', background: 'rgba(99,102,241,0.04)', borderRadius: '10px', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1.2rem' }}>🎯</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#6366f1' }}>Pipeline Stage</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Advance lead to Quotation, PI, or Sales Order when qualified</div>
                      </div>
                    </div>
                    <select
                      className="form-input"
                      style={{ width: '220px', fontWeight: 700, color: '#6366f1', borderColor: 'rgba(99,102,241,0.4)' }}
                      value={form.stage}
                      onChange={e => {
                        const newStage = e.target.value;
                        setForm(p => ({
                          ...p,
                          stage: newStage,
                          orderDocumentType: newStage === 'quotation' ? 'quotation' : newStage === 'pi' ? 'pi' : (modalDocTab === 'challan' ? 'challan' : 'tax_invoice')
                        }));
                        if (newStage === 'quotation') {
                          setModalDocTab('quotation');
                        } else if (newStage === 'pi') {
                          setModalDocTab('pi');
                        } else if (modalDocTab === 'quotation' || modalDocTab === 'pi') {
                          setModalDocTab('tax_invoice');
                        }
                      }}
                    >
                      {Object.entries(STAGE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                    </select>
                  </div>

                  {/* SECTION 1: LEAD OWNERS & ASSIGNMENT */}
                  <div style={{ padding: '16px', background: 'rgba(99,102,241,0.04)', borderRadius: '12px', border: '1px solid rgba(99,102,241,0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.2rem' }}>👤</span>
                        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0, color: '#6366f1' }}>
                          Lead Owners & Assignment
                        </h3>
                      </div>
                      <span style={{ fontSize: '0.72rem', background: 'rgba(99,102,241,0.12)', color: '#6366f1', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                        Lead Owner Module
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <label className="form-label">Lead Owner *</label>
                        {teamUsers && teamUsers.length > 0 ? (
                          <select
                            className="form-input"
                            value={form.leadOwner || ''}
                            onChange={e => {
                              const selId = e.target.value;
                              const selUser = teamUsers.find(u => u._id === selId);
                              setForm(p => ({
                                ...p,
                                leadOwner: selId,
                                leadOwnerName: selUser?.name || p.leadOwnerName,
                                salesPersonName: selUser?.name || p.salesPersonName
                              }));
                            }}
                          >
                            <option value="">-- Select Lead Owner --</option>
                            {teamUsers.map(u => (
                              <option key={u._id} value={u._id}>{u.name} ({u.role || 'Sales'})</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="form-input"
                            required
                            placeholder="e.g. Rahul Sharma"
                            value={form.leadOwnerName || form.salesPersonName || user?.name || ''}
                            onChange={e => setForm(p => ({ ...p, leadOwnerName: e.target.value, salesPersonName: e.target.value }))}
                          />
                        )}
                      </div>
                      <div>
                        <label className="form-label">Sales Representative</label>
                        <input
                          className="form-input"
                          value={form.salesPersonName || user?.name || ''}
                          onChange={e => setForm(p => ({ ...p, salesPersonName: e.target.value }))}
                          placeholder="Assigned sales rep"
                        />
                      </div>
                      <div>
                        <label className="form-label">Lead Source</label>
                        <select
                          className="form-input"
                          value={form.leadSource || 'Direct Inquiry'}
                          onChange={e => setForm(p => ({ ...p, leadSource: e.target.value }))}
                        >
                          {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2: PROSPECT & COMPANY CONTACT INFORMATION */}
                  <div style={{ padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '1.2rem' }}>🏢</span>
                      <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>
                        Prospect & Company Contact Information
                      </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label className="form-label">Customer / Company Name *</label>
                        <input
                          className="form-input"
                          required
                          value={form.customerName}
                          onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))}
                          placeholder="e.g. Acme Automation / Tata Motors"
                        />
                      </div>
                      <div>
                        <label className="form-label">Contact Number</label>
                        <input
                          className="form-input"
                          value={form.customerContact}
                          onChange={e => setForm(p => ({ ...p, customerContact: e.target.value }))}
                          placeholder="+91 9876543210"
                        />
                      </div>
                      <div>
                        <label className="form-label">Email Address</label>
                        <input
                          className="form-input"
                          type="email"
                          value={form.customerEmail}
                          onChange={e => setForm(p => ({ ...p, customerEmail: e.target.value }))}
                          placeholder="contact@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Customer Location / Address</label>
                      <input
                        className="form-input"
                        value={form.customerAddress || form.billingAddress || ''}
                        onChange={e => setForm(p => ({ ...p, customerAddress: e.target.value, billingAddress: e.target.value }))}
                        placeholder="City, State, Industrial Area..."
                      />
                    </div>
                  </div>

                  {/* SECTION 3: LEAD DETAILS (Lead Status, Opportunity Type, Rating) */}
                  <div style={{ padding: '16px', background: 'rgba(59,130,246,0.04)', borderRadius: '12px', border: '1px solid rgba(59,130,246,0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.2rem' }}>📊</span>
                        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0, color: '#3b82f6' }}>
                          Lead Details: Status, Opportunity Type & Rating
                        </h3>
                      </div>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Salesforce Lead Details</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                      <div>
                        <label className="form-label">Lead Status *</label>
                        <select
                          className="form-input"
                          value={form.leadStatus || 'new'}
                          onChange={e => setForm(p => ({ ...p, leadStatus: e.target.value }))}
                          style={{ fontWeight: 600 }}
                        >
                          {LEAD_STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.icon} {opt.label} — {opt.desc}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="form-label">Opportunity Type *</label>
                        <select
                          className="form-input"
                          value={form.opportunityType || 'new_business'}
                          onChange={e => setForm(p => ({ ...p, opportunityType: e.target.value }))}
                          style={{ fontWeight: 600 }}
                        >
                          {OPPORTUNITY_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Rating (Hot 🔥, Warm 🌤️, Cold ❄️) */}
                    <div>
                      <label className="form-label" style={{ marginBottom: '6px' }}>Rating *</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                        {RATING_OPTIONS.map(r => {
                          const isSelected = (form.rating || 'warm') === r.value;
                          return (
                            <div
                              key={r.value}
                              onClick={() => setForm(p => ({ ...p, rating: r.value }))}
                              style={{
                                border: `1.5px solid ${isSelected ? r.color : 'var(--border)'}`,
                                background: isSelected ? r.bg : 'rgba(255,255,255,0.02)',
                                borderRadius: '10px', padding: '10px 14px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px',
                                transition: 'all 0.15s'
                              }}
                            >
                              <span style={{ fontSize: '1.2rem' }}>{r.icon}</span>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: isSelected ? r.color : 'var(--text-primary)' }}>
                                  {r.label}
                                </div>
                                <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>
                                  {r.desc}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* SECTION 4: NEXT STEPS / REJECTION INFORMATION */}
                  <div style={{ padding: '16px', background: form.leadStatus === 'unqualified' ? 'rgba(239,68,68,0.05)' : 'rgba(245,158,11,0.04)', borderRadius: '12px', border: `1px solid ${form.leadStatus === 'unqualified' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.2)'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '1.2rem' }}>{form.leadStatus === 'unqualified' ? '🚫' : '🎯'}</span>
                      <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0, color: form.leadStatus === 'unqualified' ? '#ef4444' : '#d97706' }}>
                        Next Steps & Follow-Up / Rejection Information
                      </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label className="form-label">Next Steps</label>
                        <input
                          className="form-input"
                          value={form.nextSteps || ''}
                          onChange={e => setForm(p => ({ ...p, nextSteps: e.target.value }))}
                          placeholder="e.g. Share catalog & engineering drawing by Friday / arrange factory visit"
                        />
                      </div>
                      <div>
                        <label className="form-label">Next Follow-Up Date</label>
                        <input
                          className="form-input"
                          type="date"
                          value={form.nextFollowUpDate || ''}
                          onChange={e => setForm(p => ({ ...p, nextFollowUpDate: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Rejection / Disqualification Information */}
                    <div style={{ marginTop: '8px', padding: '12px', background: 'rgba(0,0,0,0.1)', borderRadius: '8px', border: '1px dashed rgba(239,68,68,0.3)' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#ef4444', marginBottom: '8px' }}>
                        Rejection Information (If Lead is Disqualified / Closed-Lost)
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '10px' }}>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.74rem' }}>Rejection Category</label>
                          <select
                            className="form-input"
                            style={{ fontSize: '0.8rem' }}
                            value={form.rejectionCategory || ''}
                            onChange={e => setForm(p => ({ ...p, rejectionCategory: e.target.value }))}
                          >
                            <option value="">-- None / Still Active --</option>
                            {REJECTION_REASONS.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.74rem' }}>Rejection Details / Disqualification Reason</label>
                          <input
                            className="form-input"
                            style={{ fontSize: '0.8rem' }}
                            value={form.rejectionReason || ''}
                            onChange={e => setForm(p => ({ ...p, rejectionReason: e.target.value }))}
                            placeholder="e.g. Budget cut, competitor price 20% lower, or requirements not matching"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 5: LIFECYCLE STAGE & REMARKS / COMMENTS */}
                  <div style={{ padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '1.2rem' }}>💬</span>
                      <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>
                        Lifecycle Stage & Remarks / Comments
                      </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label className="form-label">Lifecycle Stage</label>
                        <select
                          className="form-input"
                          value={form.lifecycleStage || 'lead'}
                          onChange={e => setForm(p => ({ ...p, lifecycleStage: e.target.value }))}
                          style={{ fontWeight: 600 }}
                        >
                          {LIFECYCLE_STAGES.map(s => (
                            <option key={s.key} value={s.key}>{s.icon} {s.label} ({s.desc})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Estimated Deal Value (₹ Approx)</label>
                        <input
                          className="form-input"
                          type="number"
                          value={form.totalAmount || 0}
                          onChange={e => {
                            const val = Number(e.target.value);
                            setForm(p => ({ ...p, totalAmount: val, grossAmount: val, grandTotal: val }));
                          }}
                          placeholder="e.g. 50000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Remarks / Comments</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        style={{ width: '100%', resize: 'vertical' }}
                        value={form.remarks || ''}
                        onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))}
                        placeholder="Record meeting minutes, client discussion, specific technical requirements, or next milestone notes..."
                      />
                    </div>
                  </div>

                  {/* SECTION 6: DRAG CHAIN SIZING REQUIREMENTS (OPTIONAL) */}
                  {isDragChain && (
                    <details style={{ background: 'rgba(249,115,22,0.03)', borderRadius: '12px', border: '1px solid rgba(249,115,22,0.2)', padding: '12px 16px' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>⚙️ Optional: Drag Chain Product Sizing & Requirements</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Click to configure</span>
                      </summary>
                      <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.78rem' }}>Model No.</label>
                          <select
                            className="form-input"
                            style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                            value={form.dragChainConfig.modelNumber}
                            onChange={e => handleModelChange(e.target.value)}
                          >
                            <option value="">-- Select Model --</option>
                            {catalogModelNumbers.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.78rem' }}>Width (Inner) *</label>
                          <select
                            className="form-input"
                            style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                            value={form.dragChainConfig.width || ''}
                            onChange={e => setForm(recalculateAll(form, 'dragChainConfig.width', e.target.value))}
                          >
                            <option value="">-- Select Width --</option>
                            {availableWidths.map(w => <option key={w} value={w}>{w} mm</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.78rem' }}>Bending Radius *</label>
                          <select
                            className="form-input"
                            style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                            value={form.dragChainConfig.radius?.replace(/^R/i, '') || ''}
                            onChange={e => setForm(recalculateAll(form, 'dragChainConfig.radius', e.target.value))}
                          >
                            <option value="">-- Select Radius --</option>
                            {availableRadii.map(r => <option key={r} value={r}>R{r}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.78rem' }}>Length per Set</label>
                          <input
                            className="form-input"
                            style={{ fontSize: '0.8rem' }}
                            placeholder="e.g. 1000 MM, 1.5 M"
                            value={form.dragChainConfig.length || ''}
                            onChange={e => setForm(recalculateAll(form, 'dragChainConfig.length', e.target.value))}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.78rem' }}>Quantity (No. of Sets)</label>
                          <input
                            className="form-input"
                            style={{ fontSize: '0.8rem' }}
                            type="number"
                            min="1"
                            step="1"
                            value={form.dragChainConfig.quantity || 1}
                            onChange={e => setForm(recalculateAll(form, 'dragChainConfig.quantity', e.target.value === '' ? '' : parseInt(e.target.value, 10)))}
                            placeholder="1"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.78rem' }}>Separator / Divider</label>
                          <select
                            className="form-input"
                            style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                            value={form.dragChainConfig.accessories || ''}
                            onChange={e => setForm(recalculateAll(form, 'dragChainConfig.accessories', e.target.value))}
                          >
                            <option value="">-- None / Not Required --</option>
                            <option value="VS">VS — Vertical Separator / Divider</option>
                            <option value="HS">HS — Horizontal Separator / Divider</option>
                            <option value="HS+VS">HS + VS — Both</option>
                          </select>
                        </div>
                      </div>
                    </details>
                  )}
                </div>
              ) : (
                <>
                  {/* Stage, Customer & Sales Person */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label className="form-label">Pipeline Stage *</label>
                      <select
                        className="form-input"
                        value={form.stage}
                        onChange={e => {
                          const newStage = e.target.value;
                          setForm(p => ({
                            ...p,
                            stage: newStage,
                            orderDocumentType: newStage === 'quotation' ? 'quotation' : newStage === 'pi' ? 'pi' : (modalDocTab === 'challan' ? 'challan' : 'tax_invoice')
                          }));
                          if (newStage === 'quotation') {
                            setModalDocTab('quotation');
                          } else if (newStage === 'pi') {
                            setModalDocTab('pi');
                          } else if (modalDocTab === 'quotation' || modalDocTab === 'pi') {
                            setModalDocTab('tax_invoice');
                          }
                        }}
                      >
                        {Object.entries(STAGE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Customer Name *</label>
                      <input className="form-input" required value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} placeholder="e.g. Acme Automation" />
                    </div>
                    <div>
                      <label className="form-label">Sales Person *</label>
                      <input className="form-input" required value={form.salesPersonName || user?.name || ''} onChange={e => setForm(p => ({ ...p, salesPersonName: e.target.value }))} placeholder="e.g. Rahul Sharma" />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isPI ? '1fr 1fr 1.2fr 1.2fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label className="form-label">Contact Number</label>
                      <input className="form-input" value={form.customerContact} onChange={e => setForm(p => ({ ...p, customerContact: e.target.value }))} placeholder="+91..." />
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <input className="form-input" type="email" value={form.customerEmail} onChange={e => setForm(p => ({ ...p, customerEmail: e.target.value }))} placeholder="sales@acme.com" />
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ margin: 0 }}>
                          {isQuotation ? 'Quotation Ref #' : isPI ? 'Proforma Number #' : 'Sales Order Ref #'}
                        </label>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(34,197,94,0.15)', color: '#16a34a', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 }}>
                          ✨ Auto-Calculated
                        </span>
                      </div>
                      <input
                        className="form-input"
                        value={isQuotation ? (form.quotationNumber || '') : isPI ? (form.piNumber || '') : form.salesOrderNumber}
                        onChange={e => {
                          const val = e.target.value;
                          setForm(p => ({
                            ...p,
                            ...(isQuotation 
                              ? { quotationNumber: val, salesOrderRef: val } 
                              : isPI 
                              ? { piNumber: val, salesOrderRef: val } 
                              : { salesOrderNumber: val })
                          }));
                        }}
                        placeholder={isQuotation ? "QTN-2026-0001" : isPI ? "PI-2026-0001" : "SO-2026-0001"}
                        style={{ background: 'rgba(34,197,94,0.05)', borderColor: 'rgba(34,197,94,0.3)', fontWeight: 700 }}
                      />
                    </div>
                    {isPI && (
                      <div>
                        <label className="form-label">Customer PO Number</label>
                        <input
                          className="form-input"
                          value={form.customerPoNumber || ''}
                          onChange={e => setForm(p => ({ ...p, customerPoNumber: e.target.value }))}
                          placeholder="Enter Customer PO Number"
                        />
                      </div>
                    )}
                  </div>

                  {/* Billing & Shipping Address with Same-as-Billing toggle */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label className="form-label">Billing Address</label>
                      <input 
                        className="form-input" 
                        value={form.billingAddress} 
                        onChange={e => {
                          const val = e.target.value;
                          setForm(p => ({
                            ...p,
                            billingAddress: val,
                            customerAddress: val,
                            shippingAddress: p.isSameAsBilling ? val : p.shippingAddress
                          }));
                        }} 
                        placeholder="Enter Customer Billing Address" 
                      />
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ margin: 0 }}>Shipping Address</label>
                        <label style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: 'var(--text-muted, #64748b)' }}>
                          <input 
                            type="checkbox" 
                            checked={form.isSameAsBilling} 
                            onChange={e => {
                              const checked = e.target.checked;
                              setForm(p => ({
                                ...p,
                                isSameAsBilling: checked,
                                shippingAddress: checked ? p.billingAddress : p.shippingAddress
                              }));
                            }} 
                          />
                          Same as Billing
                        </label>
                      </div>
                      <input 
                        className="form-input" 
                        disabled={form.isSameAsBilling}
                        value={form.shippingAddress} 
                        onChange={e => setForm(p => ({ ...p, shippingAddress: e.target.value }))} 
                        placeholder="Enter Shipping / Delivery Address" 
                        style={form.isSameAsBilling ? { opacity: 0.7, background: 'rgba(0,0,0,0.03)' } : {}}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* TAB 0 CONTENT: QUOTATION PROPOSAL SPECIFICS */}
              {isQuotation && !isLead && (
                <div style={{ padding: '16px', background: 'rgba(59,130,246,0.04)', borderRadius: '10px', border: '1px solid rgba(59,130,246,0.25)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '1.1rem' }}>📋</span>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: '#3b82f6' }}>
                      Quotation Proposal Details
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Quotation Ref No. *</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        required={isQuotation} 
                        value={form.quotationNumber || ''} 
                        onChange={e => {
                          const val = e.target.value;
                          setForm(p => ({ ...p, quotationNumber: val, salesOrderRef: val }));
                        }} 
                        placeholder="QTN-2026-001" 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Quotation Date *</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        type="date" 
                        required={isQuotation}
                        value={form.quotationDate || ''} 
                        onChange={e => setForm(p => ({ ...p, quotationDate: e.target.value }))} 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Payment Terms / Quotation Validity</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.paymentTerms || ''} 
                        onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} 
                        placeholder="e.g. 100% Advance / 30 Days Net" 
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 0.5 CONTENT: PROFORMA INVOICE (PI) SPECIFICS */}
              {isPI && !isLead && (
                <div style={{ padding: '16px', background: 'rgba(245,158,11,0.04)', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.25)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '1.1rem' }}>📄</span>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: '#d97706' }}>
                      Proforma Invoice & Commercial Terms Details
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Proforma Number *</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        required={isPI} 
                        value={form.piNumber || ''} 
                        onChange={e => {
                          const val = e.target.value;
                          setForm(p => ({ ...p, piNumber: val, salesOrderRef: val }));
                        }} 
                        placeholder="PI-2026-001" 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Proforma Date *</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        type="date" 
                        required={isPI}
                        value={form.invoiceDate || ''} 
                        onChange={e => setForm(p => ({ ...p, invoiceDate: e.target.value }))} 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Customer PO Number</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.customerPoNumber || ''} 
                        onChange={e => setForm(p => ({ ...p, customerPoNumber: e.target.value }))} 
                        placeholder="Enter Customer PO Number" 
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Preceding Quotation Ref #</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.quotationNumber || ''} 
                        onChange={e => setForm(p => ({ ...p, quotationNumber: e.target.value }))} 
                        placeholder="QTN-2026-XXXX" 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Payment Terms / Advance Terms</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.paymentTerms || ''} 
                        onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} 
                        placeholder="e.g. 100% Advance / 30 Days Net" 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Customer GSTIN</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.customerGST || ''} 
                        onChange={e => setForm(p => ({ ...p, customerGST: e.target.value }))} 
                        placeholder="27AAPCA1234F1Z5" 
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 1 CONTENT: TAX INVOICE SPECIFICS */}
              {modalDocTab === 'tax_invoice' && !isQuotation && !isPI && !isLead && (
                <div style={{ padding: '16px', background: 'rgba(59,130,246,0.04)', borderRadius: '10px', border: '1px solid rgba(59,130,246,0.2)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '1.1rem' }}>🧾</span>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: '#3b82f6' }}>
                      Tax Invoice & GST Billing Details
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Tax Invoice No. *</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} required={modalDocTab === 'tax_invoice' && !isQuotation && !isPI && !isLead} value={form.invoiceNumber} onChange={e => setForm(p => ({ ...p, invoiceNumber: e.target.value }))} placeholder="INV-2026-001" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Invoice Date</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} type="date" value={form.invoiceDate} onChange={e => setForm(p => ({ ...p, invoiceDate: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Customer GSTIN</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.customerGST} onChange={e => setForm(p => ({ ...p, customerGST: e.target.value }))} placeholder="27AAPCA1234F1Z5" />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>HSN / SAC Code</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.hsnCode} onChange={e => setForm(p => ({ ...p, hsnCode: e.target.value }))} placeholder="8483 / 3926" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>GST Supply Type</label>
                      <select className="form-input" style={{ fontSize: '0.82rem' }} value={form.isInterState ? 'inter' : 'intra'} onChange={e => setForm(p => ({ ...p, isInterState: e.target.value === 'inter' }))}>
                        <option value="intra">Intra-State (9% CGST + 9% SGST)</option>
                        <option value="inter">Inter-State (18% IGST)</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Payment Terms</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.paymentTerms} onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} placeholder="30 Days Net / Immediate" />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 1.5 CONTENT: STANDARD BILL (NON-GST) SPECIFICS */}
              {modalDocTab === 'standard_bill' && !isQuotation && !isPI && !isLead && (
                <div style={{ padding: '16px', background: 'rgba(16,185,129,0.04)', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.2)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1.1rem' }}>📄</span>
                      <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: '#10b981' }}>
                        Standard Bill (Non-GST) / Bill of Supply
                      </h3>
                    </div>
                    <span style={{ fontSize: '0.72rem', background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '2px 8px', borderRadius: '6px', fontWeight: 700, border: '1px solid rgba(16,185,129,0.3)' }}>
                      0% GST / Non-Taxable Retail Supply
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Standard Bill No. *</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        required={modalDocTab === 'standard_bill' && !isQuotation && !isPI && !isLead} 
                        value={form.invoiceNumber} 
                        onChange={e => setForm(p => ({ ...p, invoiceNumber: e.target.value }))} 
                        placeholder="BILL-2026-001" 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Bill Date</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        type="date" 
                        value={form.invoiceDate} 
                        onChange={e => setForm(p => ({ ...p, invoiceDate: e.target.value }))} 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Payment Terms</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.paymentTerms} 
                        onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} 
                        placeholder="Immediate / 30 Days Net" 
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>HSN / SAC Code (Optional)</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.hsnCode} 
                        onChange={e => setForm(p => ({ ...p, hsnCode: e.target.value }))} 
                        placeholder="8483 / 3926" 
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Billing Notes / Remarks</label>
                      <input 
                        className="form-input" 
                        style={{ fontSize: '0.82rem' }} 
                        value={form.dispatchNotes || ''} 
                        onChange={e => setForm(p => ({ ...p, dispatchNotes: e.target.value }))} 
                        placeholder="e.g. Non-taxable goods delivery / standard supply" 
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2 CONTENT: DELIVERY CHALLAN SPECIFICS */}
              {modalDocTab === 'challan' && !isQuotation && !isPI && !isLead && (
                <div style={{ padding: '16px', background: 'rgba(16,185,129,0.04)', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.2)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '1.1rem' }}>🚚</span>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: '#10b981' }}>
                      Delivery Challan & Dispatch Logistics Details
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Challan No. *</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} required={modalDocTab === 'challan' && !isQuotation && !isPI && !isLead} value={form.challanNumber} onChange={e => setForm(p => ({ ...p, challanNumber: e.target.value }))} placeholder="DC-2026-001" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Challan Date</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} type="date" value={form.challanDate} onChange={e => setForm(p => ({ ...p, challanDate: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Challan Nature / Purpose</label>
                      <select className="form-input" style={{ fontSize: '0.82rem' }} value={form.challanType} onChange={e => setForm(p => ({ ...p, challanType: e.target.value }))}>
                        <option value="against_sales_order">Delivery Against Sales Order</option>
                        <option value="supply_on_approval">Supply on Approval</option>
                        <option value="job_work">Job Work / Testing</option>
                        <option value="sample_testing">Demonstration / Sample</option>
                        <option value="returnable">Returnable Goods</option>
                        <option value="non_returnable">Non-Returnable Goods</option>
                        <option value="other">Other Supply</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Transport Mode</label>
                      <select className="form-input" style={{ fontSize: '0.82rem' }} value={form.transportMode} onChange={e => setForm(p => ({ ...p, transportMode: e.target.value }))}>
                        <option value="Road">Road Transport</option>
                        <option value="Courier">Courier / Express</option>
                        <option value="Hand Delivery">Hand Delivery</option>
                        <option value="Air">Air Cargo</option>
                        <option value="Train">Rail Cargo</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Vehicle Number</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.vehicleNumber} onChange={e => setForm(p => ({ ...p, vehicleNumber: e.target.value }))} placeholder="e.g. DL-01-AB-1234" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>E-Way Bill # / LR #</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.eWayBillNumber || form.lrNumber} onChange={e => setForm(p => ({ ...p, eWayBillNumber: e.target.value, lrNumber: e.target.value }))} placeholder="EWB or Lorry Receipt #" />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Receiver / Consignee Person</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.receiverName} onChange={e => setForm(p => ({ ...p, receiverName: e.target.value }))} placeholder="e.g. Site In-Charge" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Dispatch / Transit Remarks</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.dispatchNotes} onChange={e => setForm(p => ({ ...p, dispatchNotes: e.target.value }))} placeholder="e.g. Fragile items, deliver to gate #2" />
                    </div>
                  </div>
                </div>
              )}

              {/* Other Reference Numbers (Quotation, PI, Customer PO) - Hidden for Lead, Quotation & PI */}
              {!isQuotation && !isPI && !isLead && (
                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px' }}>📄 Preceding Reference Numbers</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <input className="form-input" style={{ fontSize: '0.8rem' }} value={form.quotationNumber} onChange={e => setForm(p => ({ ...p, quotationNumber: e.target.value }))} placeholder="Quotation #" />
                    <input className="form-input" style={{ fontSize: '0.8rem' }} value={form.piNumber} onChange={e => setForm(p => ({ ...p, piNumber: e.target.value }))} placeholder="PI #" />
                    <input className="form-input" style={{ fontSize: '0.8rem' }} value={form.customerPoNumber} onChange={e => setForm(p => ({ ...p, customerPoNumber: e.target.value }))} placeholder="Customer PO #" />
                  </div>
                </div>
              )}

              {/* DRAG CHAIN CONFIGURATION SECTION — DB-driven dropdowns & Pitch/Links/Pricing Formulas (Hidden for Leads) */}
              {isDragChain && !isLead && (
                <div style={{ padding: '16px', background: 'rgba(249,115,22,0.03)', borderRadius: '10px', border: '1px solid rgba(249,115,22,0.25)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1.2rem' }}>⚙️</span>
                      <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0, color: 'var(--primary)' }}>
                        Drag Chain Configuration & Auto Calculations
                      </h3>
                    </div>
                    <span style={{ fontSize: '0.72rem', background: 'rgba(249,115,22,0.12)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                      Formula: Links = Length(mm) ÷ Pitch(mm)
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>

                    {/* 1. Model Number — from DB */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Model No. *</label>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        value={form.dragChainConfig.modelNumber}
                        onChange={e => handleModelChange(e.target.value)}
                      >
                        <option value="">-- Select Model --</option>
                        {catalogModelNumbers.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>

                    {/* 2. Width (Inner Width) — resets on Model change */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Width (Inner) *</label>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>Reset on model change</span>
                      </div>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        value={form.dragChainConfig.width || ''}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.width', e.target.value))}
                      >
                        <option value="">-- Select Width --</option>
                        {availableWidths.map(w => <option key={w} value={w}>{w} mm</option>)}
                      </select>
                    </div>

                    {/* 3. Bending Radius — resets on Model change */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Bending Radius *</label>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>Reset on model change</span>
                      </div>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        value={form.dragChainConfig.radius?.replace(/^R/i, '') || ''}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.radius', e.target.value))}
                      >
                        <option value="">-- Select Radius --</option>
                        {availableRadii.map(r => (
                          <option key={r} value={r}>R{r}</option>
                        ))}
                      </select>
                    </div>

                    {/* 4. Type: OP / CP / SEMI */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Type (Open/Closed)</label>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        value={form.dragChainConfig.chainType}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.chainType', e.target.value))}
                      >
                        <option value="OP">OP — Open</option>
                        <option value="CP">CP — Closed</option>
                        <option value="SEMI">SEMI — Semi-Closed</option>
                      </select>
                    </div>

                    {/* 5. Pitch (mm) - Requirement 1: Ptch <> Link calculation */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Pitch (mm) 📐</label>
                        <span style={{ fontSize: '0.68rem', color: 'var(--primary)', fontWeight: 700 }}>Auto-Formula</span>
                      </div>
                      <input
                        className="form-input"
                        type="number"
                        step="any"
                        placeholder="e.g. 45.5, 55.5"
                        value={form.dragChainConfig.pitch || ''}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.pitch', e.target.value))}
                      />
                    </div>

                    {/* 6. Number of Links - Auto Calculated / Reverse Editable */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Total Links 🔗</label>
                        <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700 }}>Auto-Synced</span>
                      </div>
                      <input
                        className="form-input"
                        type="number"
                        placeholder="e.g. 22, 44"
                        value={form.dragChainConfig.totalCalculatedLinks || form.dragChainConfig.numberOfLinks || ''}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.numberOfLinks', e.target.value))}
                      />
                      {form.dragChainConfig.linksPerSet && (
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)', marginTop: '2px', display: 'block' }}>
                          ({form.dragChainConfig.linksPerSet} links/set × {form.dragChainConfig.quantity || 1} sets)
                        </span>
                      )}
                    </div>

                    {/* 7. Length (Length per Set) */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Length per Set (mm / m)</label>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>Single set length</span>
                      </div>
                      <input
                        list="chain-length-presets"
                        className="form-input"
                        style={{ fontSize: '0.8rem' }}
                        placeholder="e.g. 1000 MM, 1.5 M"
                        value={form.dragChainConfig.length}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.length', e.target.value))}
                      />
                      <datalist id="chain-length-presets">
                        <option value="500 MM" />
                        <option value="1000 MM" />
                        <option value="1500 MM" />
                        <option value="2000 MM" />
                        <option value="2500 MM" />
                        <option value="3000 MM" />
                        <option value="4000 MM" />
                        <option value="5000 MM" />
                        <option value="1.0 M" />
                        <option value="1.5 M" />
                        <option value="2.0 M" />
                        <option value="2.5 M" />
                        <option value="3.0 M" />
                      </datalist>
                    </div>

                    {/* 8. Quantity (Number of Sets required by customer) */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Quantity (No. of Sets) 📦 *</label>
                        <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700 }}>Customer Sets</span>
                      </div>
                      <input
                        className="form-input"
                        style={{ fontSize: '0.82rem', fontWeight: 700 }}
                        type="number"
                        min="1"
                        step="1"
                        placeholder="1"
                        value={form.dragChainConfig.quantity}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.quantity', e.target.value === '' ? '' : parseInt(e.target.value, 10)))}
                      />
                      <div style={{ fontSize: '0.7rem', color: 'var(--primary)', marginTop: '4px', fontWeight: 600 }}>
                        📏 Total: {form.dragChainConfig.totalMeters || (form.dragChainConfig.quantity || 1)} Meters ({form.dragChainConfig.totalCalculatedLinks || 22} Links total)
                      </div>
                    </div>

                    {/* 9. Mounting Bracket Type */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Mounting Bracket Type</label>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        value={form.dragChainConfig.mountingBracketType}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.mountingBracketType', e.target.value))}
                      >
                        <option value="">-- Select Bracket --</option>
                        <option value="B-BOX">B-BOX Type ({formatMountingBracketCode('B-BOX', form.dragChainConfig.modelNumber || '35.1')})</option>
                        <option value="L-ANGULAR">L-ANGULAR Type ({formatMountingBracketCode('L-ANGULAR', form.dragChainConfig.modelNumber || '35.1')})</option>
                        <option value="LSR(P)">LSR (P) — Plastic Angular Strain Relief ({formatMountingBracketCode('LSR(P)', form.dragChainConfig.modelNumber || '35.1')})</option>
                        <option value="LSR(M)">LSR (M) — Metal Angular Strain Relief ({formatMountingBracketCode('LSR(M)', form.dragChainConfig.modelNumber || '35.1')})</option>
                        <option value="SRP-25">SRP-25</option>
                        <option value="SRP-35">SRP-35</option>
                        <option value="SRP-50">SRP-50</option>
                      </select>
                    </div>

                    {/* 10. Separator / Divider */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Separator / Divider</label>
                        <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 700 }}>Cable Partition</span>
                      </div>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                        value={form.dragChainConfig.accessories || ''}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.accessories', e.target.value))}
                      >
                        <option value="">-- None / Not Required --</option>
                        <option value="VS">VS — Vertical Separator / Divider</option>
                        <option value="HS">HS — Horizontal Separator / Divider</option>
                        <option value="HS+VS">HS + VS — Both Horizontal & Vertical Separator / Divider</option>
                      </select>
                    </div>

                    {/* 11. Base Price / Meter - Editable (Client requirement: Base Rate / meter shouldn't fixed) */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Base Rate / Meter (₹) ✏️ *</label>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(34,197,94,0.15)', color: '#16a34a', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 }}>
                          Editable Rate
                        </span>
                      </div>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="any"
                        placeholder="e.g. 1200"
                        value={form.dragChainConfig.ratePerMeter ?? form.ratePerMeter ?? 1200}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.ratePerMeter', parseFloat(e.target.value) || 0))}
                        style={{ fontWeight: 700, color: 'var(--text-primary)' }}
                      />
                    </div>

                    {/* 12. Sales Discount % - Adjustable */}
                    <div className="form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>Sales Discount (%) 🏷️</label>
                        <span style={{ fontSize: '0.68rem', color: '#22c55e', fontWeight: 700 }}>Sales Adjustable</span>
                      </div>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        placeholder="0%"
                        value={form.dragChainConfig.discountPercent ?? form.discountPercent ?? 0}
                        onChange={e => setForm(recalculateAll(form, 'dragChainConfig.discountPercent', parseFloat(e.target.value) || 0))}
                        style={{ borderColor: 'rgba(34,197,94,0.5)', fontWeight: 700, color: '#22c55e' }}
                      />
                    </div>

                  </div>

                  {/* Live Calculation Summary Breakdown Card */}
                  <div style={{
                    marginTop: '14px', padding: '12px 16px', background: 'rgba(34,197,94,0.04)',
                    borderRadius: '10px', border: '1px solid rgba(34,197,94,0.25)', display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Gross ({form.dragChainConfig.totalMeters || 1}m × ₹{form.dragChainConfig.ratePerMeter || 1200})</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>₹{(form.grossAmount || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: '#22c55e' }}>Discount ({form.discountPercent || 0}%)</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#22c55e' }}>-₹{(form.discountAmount || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: '#3b82f6' }}>Taxable Amount</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#3b82f6' }}>₹{(form.totalAmount || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>GST ({form.taxRate || 18}%)</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>+₹{(form.gstAmount || 0).toLocaleString()}</div>
                    </div>
                    <div style={{ background: 'rgba(34,197,94,0.12)', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(34,197,94,0.3)' }}>
                      <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 700 }}>Grand Total</div>
                      <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#22c55e' }}>₹{(form.grandTotal || 0).toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Live Preview — shows the formed product code (length * set Quantity) */}
                  {form.dragChainConfig.modelNumber && (
                    <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(249,115,22,0.08)', borderRadius: '8px', border: '1px solid rgba(249,115,22,0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>📋 Order Model Code:</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 700 }}>
                          Length: Length × Set ({form.dragChainConfig.quantity || 1} sets) = {Math.round((form.dragChainConfig.totalMeters || 1) * 1000)}mm
                        </div>
                      </div>
                      <div style={{ fontSize: '0.92rem', color: 'var(--primary)', fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.5px' }}>
                        {formatDragChainOrderCode({ ...form.dragChainConfig, quantity: form.dragChainConfig.quantity })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sales Confidential Logistics & Courier Details (Hidden from Dispatch Dept, Leads, Quotations & PI) */}
              {!isQuotation && !isPI && !isLead && (
                <div style={{
                  background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: '10px', padding: '14px', marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      🚚 Logistics & Courier Details
                    </div>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', background: 'rgba(59,130,246,0.1)', padding: '2px 8px', borderRadius: '4px', border: '1px solid rgba(59,130,246,0.2)' }}>
                      🔒 Sales Only (Hidden from Dispatch)
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Courier / Transport Partner</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} placeholder="e.g. DTDC, Blue Dart, V-Trans"
                        value={form.courierPartner} onChange={e => setForm(p => ({ ...p, courierPartner: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Tracking / Docket / AWB No</label>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} placeholder="Tracking or Consignment No"
                        value={form.trackingNumber} onChange={e => setForm(p => ({ ...p, trackingNumber: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Shipping Date</label>
                      <input className="form-input" type="date" style={{ fontSize: '0.82rem' }}
                        value={form.shippingDate} onChange={e => setForm(p => ({ ...p, shippingDate: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Est. Delivery Date</label>
                      <input className="form-input" type="date" style={{ fontSize: '0.82rem' }}
                        value={form.estimatedDeliveryDate} onChange={e => setForm(p => ({ ...p, estimatedDeliveryDate: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Shipping Charges (₹)</label>
                      <input className="form-input" type="number" style={{ fontSize: '0.82rem' }} placeholder="0"
                        value={form.shippingCharges} onChange={e => setForm(p => ({ ...p, shippingCharges: Number(e.target.value) }))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Freight Terms</label>
                      <select className="form-input" style={{ fontSize: '0.82rem' }}
                        value={form.freightTerms} onChange={e => setForm(p => ({ ...p, freightTerms: e.target.value }))}>
                        <option value="">Select Freight Terms</option>
                        <option value="paid_by_us">Paid by Us (Included)</option>
                        <option value="to_pay">To Pay (Customer Pays)</option>
                        <option value="billed_to_customer">Billed to Customer Extra</option>
                        <option value="paid_by_sender">Paid by Sender</option>
                        <option value="to_pay_customer">To-Pay by Customer</option>
                        <option value="included_in_invoice">Included in Invoice</option>
                        <option value="free_shipping">Free Shipping</option>
                        <option value="fob">FOB Factory</option>
                        <option value="cif">CIF Destination</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Logistics / Delivery Remarks</label>
                    <input className="form-input" style={{ fontSize: '0.82rem' }} placeholder="e.g. Handle with care, delivery via local branch"
                      value={form.logisticRemarks} onChange={e => setForm(p => ({ ...p, logisticRemarks: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Change Reason & Audit Note (When editing) */}
              {editingId && (
                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label className="form-label" style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 700, margin: 0 }}>
                      📝 Change Reason / Audit Note (Recorded in Audit Trail)
                    </label>
                    <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>Optional</span>
                  </div>
                  <input
                    className="form-input"
                    style={{ fontSize: '0.82rem' }}
                    placeholder="e.g. Client increased length by 2m / price discount authorized per agreement"
                    value={form.changeReason || ''}
                    onChange={e => setForm(p => ({ ...p, changeReason: e.target.value }))}
                  />
                </div>
              )}

              {/* Financial & Credit Days (Hidden for Leads) */}
              {!isLead && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label className="form-label">
                      {isQuotation ? 'Quotation Value / Approx (₹)' : isPI ? 'Proforma Total Value (₹)' : modalDocTab === 'challan' ? 'Goods Value / Approx (₹)' : 'Total Taxable Amount (₹)'}
                    </label>
                    <input className="form-input" type="number" readOnly={isDragChain} value={form.totalAmount} onChange={e => handleAmountChange(e.target.value)} style={isDragChain ? { background: 'rgba(0,0,0,0.06)' } : {}} />
                  </div>
                  <div>
                    <label className="form-label">Credit Days (PDF)</label>
                    <input className="form-input" type="number" value={form.creditDays} onChange={e => setForm(p => ({ ...p, creditDays: Number(e.target.value) }))} placeholder="30" />
                  </div>
                  <div>
                    <label className="form-label">Credit Due Date</label>
                    <input className="form-input" type="date" value={form.creditDueDate} onChange={e => setForm(p => ({ ...p, creditDueDate: e.target.value }))} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <HiOutlineSave /> {
                    submitting 
                      ? 'Saving...' 
                      : isLead
                        ? (editingId ? 'Update Lead' : 'Create Lead')
                        : isQuotation
                          ? (editingId ? 'Update Quotation' : 'Create Quotation')
                          : isPI
                            ? (editingId ? 'Update Proforma Invoice' : 'Create Proforma Invoice (PI)')
                            : editingId 
                              ? 'Update Record & Log Changes' 
                              : modalDocTab === 'standard_bill'
                                ? 'Create Standard Bill (Non-GST)'
                                : modalDocTab === 'challan' 
                                  ? 'Create Delivery Challan Order' 
                                  : 'Create Tax Invoice Order'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      )}

      {approvingOrder && (
        <ApproveSalesOrderModal
          order={approvingOrder}
          onClose={() => setApprovingOrder(null)}
          onApproved={fetchData}
        />
      )}

      {/* FOLLOW-UP NOTES MODAL */}
      {showFollowUpModal && followUpRecord && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }} onClick={e => e.target === e.currentTarget && setShowFollowUpModal(false)}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '500px',
            border: '1px solid var(--border)', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                💬 Follow-ups for {followUpRecord.customerName}
              </h3>
              <button onClick={() => setShowFollowUpModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '1.2rem' }}><HiOutlineX /></button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden' }}>
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '16px', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                {followUpRecord.followUps?.length === 0 ? (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>No follow-up notes yet.</div>
                ) : (
                  followUpRecord.followUps?.map((f, i) => (
                    <div key={i} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '8px', marginBottom: '8px' }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{f.note}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                        {new Date(f.date).toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={handleAddFollowUp}>
                <div style={{ marginBottom: '12px' }}>
                  <textarea className="form-input" rows={3} required placeholder="Add follow-up / visit note..."
                    value={followUpNote} onChange={e => setFollowUpNote(e.target.value)} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowFollowUpModal(false)}>Close</button>
                  <button type="submit" className="btn btn-primary">Add Note</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* AUDIT LOG HISTORY MODAL */}
      {showAuditModal && auditRecord && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px'
        }} onClick={e => e.target === e.currentTarget && setShowAuditModal(false)}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '650px',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            border: '1px solid var(--border)', boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📜 Audit Log & Change History
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                  {auditRecord.customerName} {auditRecord.salesOrderNumber ? `• ${auditRecord.salesOrderNumber}` : ''}
                </p>
              </div>
              <button onClick={() => setShowAuditModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '1.3rem' }}><HiOutlineX /></button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '24px' }}>
              {loadingAudit ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>Loading audit trail...</div>
              ) : (!auditRecord.auditLogs || auditRecord.auditLogs.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>
                  No historical changes logged for this record yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {auditRecord.auditLogs.map((log, idx) => {
                    const actionColors = {
                      created: { bg: 'rgba(99,102,241,0.15)', color: '#6366f1', label: 'CREATED' },
                      updated: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', label: 'UPDATED' },
                      approved: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', label: 'ADMIN APPROVED' },
                      rejected: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', label: 'ADMIN REJECTED' },
                      stage_changed: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'STAGE CHANGED' }
                    };
                    const ac = actionColors[log.action] || actionColors.updated;

                    return (
                      <div key={idx} style={{
                        background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)',
                        borderRadius: '10px', padding: '14px', position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              background: ac.bg, color: ac.color, padding: '2px 8px',
                              borderRadius: '6px', fontSize: '0.7rem', fontWeight: 800
                            }}>
                              {ac.label}
                            </span>
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                              👤 {log.changedByName || log.changedBy?.name || 'Authorized User'}
                            </span>
                            {(log.changedByRole || log.changedBy?.role) && (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>
                                ({log.changedByRole || log.changedBy?.role})
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                            🕒 {new Date(log.changedAt).toLocaleString()}
                          </span>
                        </div>

                        {log.note && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--primary)', fontStyle: 'italic', marginBottom: '8px' }}>
                            💬 "{log.note}"
                          </div>
                        )}

                        {log.changesSummary && log.changesSummary.length > 0 && (
                          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            {log.changesSummary.map((ch, i) => (
                              <li key={i} style={{ marginBottom: '3px' }}>{ch}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowAuditModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENT PREVIEW & PRINT MODAL */}
      {showPreviewModal && previewRecord && (() => {
        const isQuote = previewRecord.stage === 'quotation' || previewRecord.orderDocumentType === 'quotation';
        const isPi = previewRecord.stage === 'pi' || previewRecord.orderDocumentType === 'pi';
        const isStandardBill = previewRecord.orderDocumentType === 'standard_bill' || previewRecord.orderDocumentType === 'non_gst_bill';
        const isChallanDoc = previewRecord.orderDocumentType === 'challan' || (!previewRecord.orderDocumentType && previewRecord.challanNumber && !previewRecord.invoiceNumber);
        
        const docTitle = isQuote 
          ? 'SALES QUOTATION / PRICE PROPOSAL' 
          : isPi 
          ? 'PROFORMA INVOICE (PI)' 
          : isStandardBill 
          ? 'STANDARD BILL / RETAIL INVOICE (NON-GST)' 
          : isChallanDoc 
          ? 'DELIVERY CHALLAN (GOODS MOVEMENT)' 
          : 'TAX INVOICE (GST)';

        const docNumber = isQuote 
          ? (previewRecord.quotationNumber || previewRecord.salesOrderNumber || 'QTN-DRAFT')
          : isPi 
          ? (previewRecord.piNumber || previewRecord.salesOrderNumber || 'PI-DRAFT')
          : isStandardBill
          ? (previewRecord.invoiceNumber || previewRecord.salesOrderNumber || 'BILL-DRAFT')
          : isChallanDoc 
          ? (previewRecord.challanNumber || previewRecord.salesOrderNumber || 'DC-DRAFT')
          : (previewRecord.invoiceNumber || previewRecord.salesOrderNumber || 'INV-DRAFT');

        const docDate = previewRecord.invoiceDate || previewRecord.quotationDate || previewRecord.challanDate || previewRecord.createdAt;
        const cfg = previewRecord.items?.[0]?.dragChainConfig || previewRecord.dragChainConfig || {};

        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px'
          }} onClick={e => e.target === e.currentTarget && setShowPreviewModal(false)}>
            <div style={{
              background: '#fff', color: '#1e293b', borderRadius: '16px', width: '100%', maxWidth: '850px',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
              position: 'relative', fontFamily: 'Inter, system-ui, sans-serif'
            }} onClick={e => e.stopPropagation()}>
              {/* Header Action Bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 28px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.4rem' }}>
                    {isQuote ? '📜' : isPi ? '📑' : isStandardBill ? '📄' : isChallanDoc ? '🚚' : '🧾'}
                  </span>
                  <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a' }}>
                    Document Preview & Print
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => window.print()} 
                    className="btn btn-primary" 
                    style={{ background: '#2563eb', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    <HiOutlinePrinter /> Print / Save as PDF
                  </button>
                  <button 
                    onClick={() => setShowPreviewModal(false)} 
                    style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#64748b', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.2rem' }}
                  >
                    <HiOutlineX />
                  </button>
                </div>
              </div>

              {/* Printable Document Sheet Body */}
              <div style={{ padding: '24px 28px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
                <div id="printable-sales-document" style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '28px', background: '#fff' }}>
                {/* Title Banner */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '16px', marginBottom: '20px' }}>
                  <div>
                    <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>
                      {user?.organizationId?.name || 'TRACKBELLS AUTOMATION'}
                    </h1>
                    <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '4px', maxWidth: '350px' }}>
                      {user?.organizationId?.address || 'Industrial Area, Manufacturing Hub'}
                    </div>
                    {user?.organizationId?.gstin && (
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>
                        GSTIN: {user.organizationId.gstin}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      display: 'inline-block', padding: '6px 14px', borderRadius: '6px',
                      background: isStandardBill ? '#ecfdf5' : isChallanDoc ? '#f5f3ff' : isQuote ? '#eff6ff' : isPi ? '#fffbeb' : '#eff6ff',
                      color: isStandardBill ? '#059669' : isChallanDoc ? '#7c3aed' : isQuote ? '#2563eb' : isPi ? '#d97706' : '#2563eb',
                      border: `1px solid ${isStandardBill ? '#a7f3d0' : isChallanDoc ? '#ddd6fe' : isQuote ? '#bfdbfe' : isPi ? '#fde68a' : '#bfdbfe'}`,
                      fontWeight: 900, fontSize: '0.95rem', letterSpacing: '0.5px'
                    }}>
                      {docTitle}
                    </div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginTop: '8px' }}>
                      Doc No: {docNumber}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>
                      Date: {new Date(docDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </div>

                {/* Customer & Billing / Shipping Details */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {isChallanDoc ? 'CONSIGNEE / RECIPIENT' : 'BILLED TO:'}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{previewRecord.customerName}</div>
                    <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: '3px' }}>
                      {previewRecord.billingAddress || previewRecord.customerAddress || 'Address on file'}
                    </div>
                    {previewRecord.customerContact && (
                      <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '2px' }}>
                        📞 Contact: {previewRecord.customerContact}
                      </div>
                    )}
                    {previewRecord.customerGST && (
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb', marginTop: '3px' }}>
                        GSTIN: {previewRecord.customerGST}
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {isChallanDoc ? 'DISPATCH & LOGISTICS' : 'SHIPPED TO / REF:'}
                    </div>
                    {isChallanDoc ? (
                      <>
                        <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a' }}>
                          🚚 Vehicle: {previewRecord.vehicleNumber || 'Assigned Carrier'}
                        </div>
                        <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: '2px' }}>
                          Mode: {previewRecord.transportMode || 'Road'} | Purpose: {previewRecord.challanType?.replace(/_/g, ' ') || 'Supply'}
                        </div>
                        {(previewRecord.lrNumber || previewRecord.eWayBillNumber) && (
                          <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: '2px' }}>
                            LR/EWB #: {previewRecord.lrNumber || previewRecord.eWayBillNumber}
                          </div>
                        )}
                        {previewRecord.receiverName && (
                          <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: '2px' }}>
                            Receiver: {previewRecord.receiverName}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                          {previewRecord.shippingAddress || previewRecord.billingAddress || previewRecord.customerAddress || 'Same as billing address'}
                        </div>
                        {previewRecord.salesOrderNumber && (
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>
                            Sales Order: {previewRecord.salesOrderNumber}
                          </div>
                        )}
                        {previewRecord.customerPoNumber && (
                          <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                            Customer PO: {previewRecord.customerPoNumber}
                          </div>
                        )}
                        {previewRecord.paymentTerms && (
                          <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                            Payment Terms: {previewRecord.paymentTerms}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Items Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#0f172a', color: '#fff' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderRadius: '4px 0 0 0' }}>#</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Item Description & Specification</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>HSN</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>Qty</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Rate (₹)</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Taxable (₹)</th>
                      {!isStandardBill && !isChallanDoc && <th style={{ padding: '8px 12px', textAlign: 'right' }}>GST %</th>}
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderRadius: '0 4px 0 0' }}>Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((previewRecord.items && previewRecord.items.length > 0) ? previewRecord.items : [previewRecord]).map((item, idx) => {
                      const itemCfg = item.dragChainConfig || cfg;
                      const specDesc = formatDragChainOrderCode(itemCfg) || item.description || item.modelNumber || 'Standard Industrial Product';
                      const bracketDesc = formatMountingBracketCode(itemCfg.mountingBracketType, itemCfg.modelNumber || item.modelNumber || '');
                      const itemQty = item.quantity || itemCfg.quantity || 1;
                      const itemRate = item.unitPrice || previewRecord.ratePerMeter || itemCfg.ratePerMeter || 1200;
                      const itemGross = previewRecord.grossAmount || (itemQty * itemRate);
                      const itemTaxable = previewRecord.totalAmount || itemGross;
                      const itemTotal = isStandardBill || isChallanDoc ? itemTaxable : (previewRecord.grandTotal || itemTaxable);

                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{idx + 1}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ fontWeight: 800, color: '#0f172a' }}>
                              {item.modelNumber || itemCfg.modelNumber || 'Standard Product'}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: '2px' }}>
                              {specDesc}
                            </div>
                            {bracketDesc && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                Mounting Bracket: {bracketDesc}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#64748b', verticalAlign: 'top' }}>
                            {previewRecord.hsnCode || '8483'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, verticalAlign: 'top' }}>
                            {itemQty} {item.unit || (isDragChain ? 'Mtr' : 'Pcs')}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#475569', verticalAlign: 'top' }}>
                            ₹{Number(itemRate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, verticalAlign: 'top' }}>
                            ₹{Number(itemTaxable).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          {!isStandardBill && !isChallanDoc && (
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#64748b', verticalAlign: 'top' }}>
                              {previewRecord.taxRate ?? 18}%
                            </td>
                          )}
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#0f172a', verticalAlign: 'top' }}>
                            ₹{Number(itemTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Financial Totals & Signatures */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '20px', marginTop: '16px' }}>
                  <div>
                    <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>
                        TERMS & CONDITIONS / NOTES:
                      </div>
                      <div style={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>
                        1. Goods once sold will not be taken back without prior authorization.<br />
                        2. {isStandardBill ? 'Standard Bill of Supply issued without GST charge (0% Tax / Exempted).' : isChallanDoc ? 'Goods dispatched for transportation/delivery purpose only.' : 'GST charged as per applicable Govt GST slabs.'}<br />
                        3. Payment Due: {previewRecord.creditDays || 30} Days ({previewRecord.creditDueDate ? new Date(previewRecord.creditDueDate).toLocaleDateString('en-IN') : 'From Invoice Date'}).
                      </div>
                    </div>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '6px' }}>
                      <span>Subtotal (Gross):</span>
                      <span style={{ fontWeight: 600 }}>₹{Number(previewRecord.grossAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>

                    {Number(previewRecord.discountAmount) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#16a34a', marginBottom: '6px' }}>
                        <span>Discount ({previewRecord.discountPercent}%):</span>
                        <span>-₹{Number(previewRecord.discountAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '6px' }}>
                      <span>Taxable Amount:</span>
                      <span style={{ fontWeight: 700 }}>₹{Number(previewRecord.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>

                    {isStandardBill || isChallanDoc ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#059669', marginBottom: '6px', fontWeight: 600 }}>
                        <span>GST (0% Non-GST / Exempt):</span>
                        <span>₹0.00</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#2563eb', marginBottom: '6px', fontWeight: 600 }}>
                        <span>GST ({previewRecord.taxRate ?? 18}%):</span>
                        <span>₹{Number(previewRecord.gstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}

                    {Number(previewRecord.shippingCharges) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '6px' }}>
                        <span>Shipping / Freight:</span>
                        <span>₹{Number(previewRecord.shippingCharges).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}

                    <div style={{ borderTop: '2px solid #0f172a', paddingTop: '8px', marginTop: '6px', display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', fontWeight: 900, color: '#0f172a' }}>
                      <span>Grand Total:</span>
                      <span>₹{Number(previewRecord.grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Footer Signature */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Generated by: {user?.name || 'Sales Officer'} | System Timestamp: {new Date().toLocaleString('en-IN')}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '180px', borderBottom: '1px solid #0f172a', marginBottom: '6px' }}></div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a' }}>Authorized Signatory</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>For {user?.organizationId?.name || 'TrackBells'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    })()}
    </div>
  );
}
